<template>
  <div>
    <!-- Header / new target button -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="d-flex align-items-center py-1">
        <h4 class="mb-0">
          <feather-icon
            icon="TargetIcon"
            size="20"
            class="text-primary mr-1"
          />
          Hedefler ({{ metaData.total }})
        </h4>
        <b-button
          variant="primary"
          class="ml-auto"
          @click="openCreateModal"
        >
          <feather-icon icon="PlusIcon" /> Yeni Hedef
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Filter -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="py-1">
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <label class="font-small-3 text-muted">Durum</label>
            <b-form-select
              v-model="filter.status"
              :options="statusOptions"
              @change="fetchList"
            />
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <label class="font-small-3 text-muted">Ad / Açıklama</label>
            <b-form-input
              v-model="filter.search"
              placeholder="ara..."
              @input="onSearchInput"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Target table -->
    <b-card no-body>
      <b-table
        responsive="sm"
        :fields="fields"
        :items="dataList"
        :busy="loading"
        striped
        show-empty
        empty-text="Henüz hedef yok. 'Yeni Hedef' ile başlayın."
      >
        <template #cell(name)="data">
          <div class="font-weight-bold">
            {{ data.item.name }}
          </div>
          <div
            v-if="data.item.description"
            class="font-small-3 text-muted"
          >
            {{ truncate(data.item.description, 80) }}
          </div>
        </template>
        <template #cell(profit)="data">
          <div class="font-small-3">
            <strong>{{ formatNum(data.item.profitTarget, 2) }}{{ data.item.profitTargetIsPct ? '%' : '$' }}</strong>
          </div>
          <div class="font-small-2 text-muted">
            Sermaye: {{ formatNum(data.item.initialCapital, 2) }} ·
            Max zarar: {{ data.item.maxLossLimit !== null ? formatNum(data.item.maxLossLimit, 2) : '—' }}
          </div>
        </template>
        <template #cell(progress)="data">
          <div
            :class="(Number(data.item.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'"
            class="font-weight-bold"
          >
            {{ (Number(data.item.realizedPnl) || 0) >= 0 ? '+' : '' }}{{ formatNum(data.item.realizedPnl, 4) }}
          </div>
          <div class="font-small-3 text-muted">
            Realize
          </div>
        </template>
        <template #cell(bots)="data">
          <b-badge variant="light-secondary">
            {{ (data.item.bots || []).length }} bot
          </b-badge>
          <div
            v-if="(data.item.bots || []).length"
            class="font-small-2 text-muted mt-1"
          >
            {{ countBotsByStatus(data.item.bots, 'RUNNING') }} çalışıyor
          </div>
        </template>
        <template #cell(window)="data">
          <div class="font-small-3">
            {{ formatDate(data.item.startAt) }}
          </div>
          <div class="font-small-3 text-muted">
            → {{ formatDate(data.item.endAt) }}
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ data.item.status }}
          </b-badge>
          <div
            v-if="data.item.autoRepeat"
            class="font-small-2 text-info mt-1"
          >
            <feather-icon icon="RepeatIcon" /> Tekrar
            <span v-if="cycleCountOf(data.item) > 1">· #{{ cycleCountOf(data.item) }}</span>
          </div>
        </template>
        <template #cell(actions)="data">
          <div class="text-right">
            <b-button
              v-if="data.item.status === 'ACTIVE'"
              variant="outline-warning"
              size="sm"
              class="mr-1"
              @click="onStop(data.item.id)"
            >
              <feather-icon icon="StopCircleIcon" /> Durdur
            </b-button>
            <b-button
              v-else-if="canStart(data.item)"
              variant="outline-success"
              size="sm"
              class="mr-1"
              @click="onStart(data.item.id)"
            >
              <feather-icon icon="PlayCircleIcon" /> Aktifleştir
            </b-button>
            <b-button
              variant="outline-info"
              size="sm"
              class="mr-1"
              @click="onShowDetail(data.item)"
            >
              <feather-icon icon="EyeIcon" /> Detay
            </b-button>
            <b-button
              variant="outline-success"
              size="sm"
              class="mr-1"
              title="Aktif edge'lerden otomatik bot serisi yarat"
              @click="openProvisionModal(data.item)"
            >
              <feather-icon icon="CpuIcon" /> Bot Ekle
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-1"
              :disabled="data.item.status === 'ACTIVE'"
              :title="data.item.status === 'ACTIVE' ? 'Aktif hedef için sadece detay aç' : 'Düzenle'"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="Edit2Icon" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              :disabled="data.item.status === 'ACTIVE'"
              @click="onRemove(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- ─── Create / Edit Modal ─────────────────────────────────────────────── -->
    <b-modal
      v-model="editorModal.show"
      :title="editorModal.id ? `Hedefi Düzenle — ${form.name}` : 'Yeni Hedef'"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-alert
        v-if="editorModal.id && editorModal.locked"
        show
        variant="warning"
        class="py-1 px-1 mb-2"
      >
        <small>
          <feather-icon icon="LockIcon" /> Hedef aktif. Sadece açıklama, bitiş tarihi ve auto-repeat değiştirilebilir.
        </small>
      </b-alert>
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Hedef Adı *</label>
          <b-form-input
            v-model="form.name"
            placeholder="Kasım Ayı Hedefi"
            :disabled="editorModal.locked"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Başlangıç Sermayesi *</label>
          <b-form-input
            v-model.number="form.initialCapital"
            type="number"
            step="10"
            min="0"
            :disabled="editorModal.locked"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Kar Hedefi *</label>
          <b-input-group>
            <b-form-input
              v-model.number="form.profitTarget"
              type="number"
              step="0.1"
              min="0"
              :disabled="editorModal.locked"
            />
            <b-input-group-append>
              <b-form-select
                v-model="form.profitTargetIsPct"
                :options="[{value:true,text:'%'},{value:false,text:'$'}]"
                :disabled="editorModal.locked"
              />
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Max Zarar Limiti</label>
          <b-form-input
            v-model.number="form.maxLossLimit"
            type="number"
            step="10"
            min="0"
            placeholder="opsiyonel"
            :disabled="editorModal.locked"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Tahmini Getiri</label>
          <b-form-input
            v-model.number="form.expectedReturn"
            type="number"
            step="10"
            placeholder="opsiyonel"
            :disabled="editorModal.locked"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Başlangıç Tarihi</label>
          <b-form-input
            v-model="form.startAt"
            type="date"
            :disabled="editorModal.locked"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Bitiş Tarihi</label>
          <b-form-input
            v-model="form.endAt"
            type="date"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Açıklama</label>
          <b-form-textarea
            v-model="form.description"
            rows="2"
            placeholder="opsiyonel"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="form.autoRepeat"
            switch
          >
            <strong>Otomatik tekrar</strong> — hedef tamamlandığında yeni bir cycle başlat
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <div class="border rounded bg-light p-1">
            <div class="d-flex flex-wrap align-items-center mb-1">
              <div>
                <strong>Exit modu</strong>
                <div class="text-muted font-small-3">
                  Provisioning sırasında guardian, break-even, partial, trail ve step stop bu baseline’dan türetilir.
                </div>
              </div>
              <b-badge
                v-if="editorModal.strategySourceMode"
                :variant="form.options.exitGovernor.mode === editorModal.strategySourceMode ? 'light-success' : 'light-warning'"
                class="ml-auto"
              >
                {{ form.options.exitGovernor.mode === editorModal.strategySourceMode ? 'Strategy mode miras aliniyor' : 'Target override aktif' }}
              </b-badge>
            </div>
            <b-form-select
              v-model="form.options.exitGovernor.mode"
              :options="exitGovernorModeOptions"
              :disabled="editorModal.locked"
              @change="onTargetExitModeChange"
            />
            <div class="d-flex flex-wrap align-items-center mt-50">
              <b-badge
                v-if="editorModal.strategySourceMode"
                :variant="form.options.exitGovernor.mode === editorModal.strategySourceMode ? 'light-success' : 'light-warning'"
                class="mr-50 mb-50"
              >
                {{ form.options.exitGovernor.mode === editorModal.strategySourceMode ? 'Strategy baseline' : 'Target override' }}
              </b-badge>
              <b-badge
                v-if="targetEntryPresetState.lastAppliedLabel"
                variant="light-info"
                class="mr-50 mb-50"
              >
                Preset: {{ targetEntryPresetState.lastAppliedLabel }}
              </b-badge>
              <b-badge
                v-if="targetEntryPresetState.isDirty"
                variant="light-warning"
                class="mr-50 mb-50"
              >
                Draft presetten çıktı
              </b-badge>
              <b-button
                v-if="editorModal.strategySourceMode && form.options.exitGovernor.mode !== editorModal.strategySourceMode"
                size="sm"
                variant="outline-warning"
                class="ml-auto mb-50"
                @click="applyTargetExitGovernorMode(editorModal.strategySourceMode)"
              >
                <feather-icon icon="RotateCcwIcon" /> Mode baseline’ını yeniden uygula
              </b-button>
            </div>
            <div class="text-muted font-small-3 mt-1">
              <div
                v-for="line in targetExitModeSummary"
                :key="line"
                class="mb-50"
              >
                <b-badge variant="light-info">
                  {{ line }}
                </b-badge>
              </div>
              <div
                v-for="line in targetExitCooldownSummary"
                :key="line"
                class="mb-50"
              >
                <b-badge variant="light-info">
                  {{ line }}
                </b-badge>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Risk yönetimi -->
      <div class="mt-2">
        <b-button
          v-b-toggle.target-risk-options
          variant="flat-secondary"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ShieldIcon" /> Risk Yönetimi (opsiyonel)
        </b-button>
        <b-collapse
          id="target-risk-options"
          class="mt-1"
          :visible="form.options.lossCoverStop || form.options.allLosingAutoStop || form.options.autoRescanOnClose || form.options.targetSignalPoolEnabled || form.options.adaptiveAvailableMargin.enabled || form.options.dailyProfitGivebackGuard.enabled || form.options.marketDirectionConsensus.enabled || form.options.positionGuardian.enabled === false || form.options.positionGuardian.recoveryAssistEnabled === true || form.options.positionGuardian.lossInsuranceEnabled === true"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-form-checkbox
              v-model="form.options.lossCoverStop"
              switch
              class="mb-1"
            >
              <strong>Loss-cover stop</strong> — bir bot zararda ama diğer botlar bunu kapsıyorsa
              (toplam ≥ 0) zarar edeni durdur, net karı kilitle.
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.options.positionGuardian.enabled"
              switch
              class="mb-1"
            >
              <strong>Position Guardian</strong> — kapalıysa Guardian bu hedefin botları ve açık pozisyonları ile ilgilenmez.
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.options.positionGuardian.lossInsuranceEnabled"
              switch
              class="mb-1"
            >
              <strong>Zarar Sigortası</strong> — kârdan sanal prim ayırır, açık hasarı izler ve hasar varken yeni işlem riskini azaltır.
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.options.positionGuardian.recoveryAssistEnabled"
              switch
              class="mb-1"
            >
              <strong>Recovery Assist</strong> — aktif edge havuzundan aynı yönlü başka bir coin seçip ters yön recovery üretir.
            </b-form-checkbox>
            <b-form-checkbox
              :checked="form.options.positionGuardian.recoveryAssistMode === 'AUTO_OPEN'"
              switch
              class="mb-1"
              @change="form.options.positionGuardian.recoveryAssistMode = $event ? 'AUTO_OPEN' : 'SUGGEST_ONLY'"
            >
              <strong>Recovery otomatik açsın</strong> — uygunsa testnet/live üzerinde recovery pozisyonunu Guardian açar.
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.options.allLosingAutoStop"
              switch
            >
              <strong>All-losing auto-stop</strong> — tüm botlar cycle'da zarardaysa otomatik durdur
              (her ~5 dk kontrol edilir).
            </b-form-checkbox>
            <hr class="my-1">
            <b-form-checkbox
              v-model="form.options.adaptiveAvailableMargin.enabled"
              switch
              class="mb-1"
            >
              <strong>Adaptif kullanılabilir margin</strong> — bakiye hedef marginini karşılamıyorsa, emri açılabilir geçici margine düşürür; kâr geldikçe tekrar hedef margine yaklaşır.
            </b-form-checkbox>
            <b-row
              v-if="form.options.adaptiveAvailableMargin.enabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min margin %</label>
                <b-form-input
                  v-model.number="form.options.adaptiveAvailableMargin.minMarginPercent"
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Güvenlik payı %</label>
                <b-form-input
                  v-model.number="form.options.adaptiveAvailableMargin.safetyBufferPercent"
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                />
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-form-checkbox
              v-model="form.options.dailyProfitGivebackGuard.enabled"
              switch
              class="mb-1"
            >
              <strong>Gün içi kâr geri verme koruması</strong> — hedef gün içinde kârı belirlenen oranda geri verirse yeni girişleri gün sonuna kadar keser.
            </b-form-checkbox>
            <b-row
              v-if="form.options.dailyProfitGivebackGuard.enabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Silahlanma kârı USDT</label>
                <b-form-input
                  v-model.number="form.options.dailyProfitGivebackGuard.minProfitToArmUsdt"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Max geri verme %</label>
                <b-form-input
                  v-model.number="form.options.dailyProfitGivebackGuard.maxGivebackPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-form-checkbox
              v-model="form.options.targetSignalPoolEnabled"
              switch
              class="mb-1"
            >
              <strong>bot müsait tüm edgeleri alabilsin.</strong>
            </b-form-checkbox>
            <b-row
              v-if="form.options.targetSignalPoolEnabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Pool max edge</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.maxEdges"
                  type="number"
                  min="1"
                  max="50"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min score</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minScoreFinal"
                  type="number"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min WR %</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minWinRate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min net EV</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minNetEv"
                  type="number"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min trade</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minTrades"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">MFE örnek</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minMfeSamples"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">+0,50 MFE %</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minMfe50Rate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">+0,75 MFE %</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minMfe75Rate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Avg MFE %</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.minAvgMfe"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Coin cooldown dk</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.symbolCooldownMinutes"
                  type="number"
                  min="0"
                  step="5"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Aynı yön max açık</label>
                <b-form-input
                  v-model.number="form.options.targetSignalPoolFilters.maxSameSideOpenPositions"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox v-model="form.options.targetSignalPoolFilters.rejectLastExitSl">
                  Son işlem SL ise alma
                </b-form-checkbox>
              </b-col>
            </b-row>
            <hr class="my-1">
            <div class="d-flex align-items-center mb-1">
              <strong class="mb-0 d-block">Sinyal giriş kuralları</strong>
              <b-button
                size="sm"
                variant="outline-primary"
                class="ml-auto"
                :disabled="editorEntryPresetState.loading"
                @click="refreshTargetEntryPresets"
              >
                <b-spinner
                  v-if="editorEntryPresetState.loading"
                  small
                />
                <feather-icon
                  v-else
                  icon="RefreshCwIcon"
                />
                Öneri yenile
              </b-button>
            </div>
            <small class="text-muted d-block mb-1">
              Bot sinyali gördükten sonra kaç kontrol bekleyeceğini ve fiyat kaçmışsa/ters sapmışsa girişin engellenip engellenmeyeceğini belirler.
            </small>
            <b-form-checkbox
              v-model="editorEntryPresetState.autoApplyEnabled"
              switch
              class="mb-1"
              @change="onTargetEntryAutoApplyChange"
            >
              Önerileri otomatik işle
            </b-form-checkbox>
            <div class="text-muted font-small-3 mb-1">
              <span v-if="editorEntryPresetState.autoApplyEnabled">
                Tüm öneriler draft'a otomatik işlenir; elle değiştirirsen preset baseline’ından sapma oluşur.
              </span>
              <span v-else>
                Öneriyi manuel uygularsın; istersen basit bir tıkla baseline’a dönebilirsin.
              </span>
            </div>
            <div
              v-if="editorEntryPresetState.rows.length"
              class="mb-1"
            >
              <div
                v-for="(row, index) in editorEntryPresetState.rows"
                :key="row.preset.id"
                class="border rounded bg-white px-1 py-50 mb-50"
              >
                <div class="d-flex align-items-center">
                  <div class="min-w-0">
                    <b-badge :variant="entryPresetVariant(row.severity)">
                      {{ entryPresetLabel(row.severity) }}
                    </b-badge>
                    <b-badge
                      v-if="index === 0"
                      variant="light-primary"
                      class="ml-50"
                    >
                      One cikan
                    </b-badge>
                    <b-badge
                      :variant="entryPresetScopeVariant(row.preset.scope)"
                      class="ml-50"
                    >
                      {{ entryPresetScopeLabel(row.preset.scope) }}
                    </b-badge>
                    <b-badge
                      v-if="isTargetEntryPresetApplied(row)"
                      variant="light-success"
                      class="ml-50"
                    >
                      İşlendi
                    </b-badge>
                    <strong class="ml-50">{{ row.preset.label }}</strong>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    class="ml-auto"
                    :disabled="isTargetEntryPresetApplied(row)"
                    @click="applyTargetEntryPreset(row)"
                  >
                    {{ isTargetEntryPresetApplied(row) ? 'İşlendi' : "Draft'e işle" }}
                  </b-button>
                </div>
                <div class="font-small-3 text-muted mt-50">
                  {{ row.title }}
                </div>
                <div class="font-small-3 text-primary">
                  Neden onde: {{ row.priorityReason }}
                </div>
                <div class="font-small-3 text-muted">
                  Uygular: {{ row.actionSummary }}
                </div>
                <div class="font-small-3 text-muted">
                  Amaç: {{ row.recommendationShort }}
                </div>
                <div class="font-small-3 text-muted">
                  {{ row.patchCount }} patch · {{ row.preset.summary }}
                </div>
              </div>
            </div>
            <b-row class="mb-1">
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Onay bekleme</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.confirmationTicks"
                  type="number"
                  min="0"
                  max="5"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks sinyal yaşı</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.maxSignalAgeBars"
                  type="number"
                  min="0"
                  max="10"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min lehe %</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.minFavorableMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks kaçmış %</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.maxFavorableMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks ters %</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.maxAdverseMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox
                  v-model="form.options.signalEntry.routerEnabled"
                  switch
                >
                  Giriş router
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min giriş skoru</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.minEntryScore"
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min kalan ödül/risk</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.minRemainingRewardRisk"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Geç sinyal başlangıcı %</label>
                <b-form-input
                  v-model.number="form.options.signalEntry.lateSignalPenaltyStartPct"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox
                  v-model="form.options.signalEntry.familyWeightEnabled"
                  switch
                >
                  Aile performansı
                </b-form-checkbox>
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-form-checkbox
              v-model="form.options.marketDirectionConsensus.enabled"
              switch
              class="mb-1"
            >
              <strong>Piyasa yönü onayı</strong> — diğer aktif edge sinyallerinin çoğunluğu aynı yöndeyse girişe izin ver.
            </b-form-checkbox>
            <b-row
              v-if="form.options.marketDirectionConsensus.enabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min sinyal</label>
                <b-form-input
                  v-model.number="form.options.marketDirectionConsensus.minSignals"
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Aynı yön %</label>
                <b-form-input
                  v-model.number="form.options.marketDirectionConsensus.requiredAgreementPercent"
                  type="number"
                  min="50"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Tazelik (mum)</label>
                <b-form-input
                  v-model.number="form.options.marketDirectionConsensus.freshnessBars"
                  type="number"
                  min="0.25"
                  max="10"
                  step="0.25"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Kararlılık</label>
                <b-form-input
                  v-model.number="form.options.marketDirectionConsensus.stabilityChecks"
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                />
              </b-col>
              <b-col
                cols="12"
                md="4"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Yetersiz veri</label>
                <b-form-select
                  v-model="form.options.marketDirectionConsensus.insufficientDataAction"
                  :options="consensusInsufficientOptions"
                />
              </b-col>
            </b-row>
            <hr class="my-1">
            <b-form-checkbox
              v-model="form.options.strategySafetyGovernor.enabled"
              switch
              class="mb-1"
            >
              <strong>Strateji güvenlik yöneticisi</strong> — risk kapalı olsa bile pozisyon boyunu ve kötüleşen giriş ailelerini otomatik frenler.
            </b-form-checkbox>
            <b-alert
              v-if="strategySafetyGovernorCoordinationSummaryLines.length"
              show
              variant="light-info"
              class="mb-1 py-50"
            >
              <div
                v-for="line in strategySafetyGovernorCoordinationSummaryLines"
                :key="line"
                class="font-small-3"
              >
                {{ line }}
              </div>
            </b-alert>
            <b-badge
              variant="light-primary"
              class="mb-1"
            >
              {{ strategySafetyGovernorCoordinationDecisionValue.kindLabel }} · {{ strategySafetyGovernorCoordinationDecisionValue.label }} · {{ strategySafetyGovernorCoordinationDecisionValue.text }}
            </b-badge>
            <div class="text-muted font-small-3 mb-1">
              {{ strategySafetyGovernorCoordinationStateValue.decisionSummary }}
            </div>
            <div class="text-muted font-small-3 mb-1">
              {{ strategySafetyGovernorCoordinationDecisionValue.detail }}
            </div>
            <div
              v-for="line in strategySafetyGovernorCoordinationStateValue.axisReasons"
              :key="`axis-${line.code}`"
              class="text-muted font-small-3 mb-50"
            >
              {{ line.label }} · {{ line.text }}
            </div>
            <div
              v-for="line in strategySafetyGovernorCoordinationStateValue.conflictReasons"
              :key="line.code"
              class="text-muted font-small-3 mb-50"
            >
              {{ line.label }} · {{ line.text }}
            </div>
            <b-row
              v-if="form.options.strategySafetyGovernor.enabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Risk kapalı max zarar</label>
                <b-form-input
                  v-model.number="form.options.strategySafetyGovernor.maxRiskUsdtWhenRiskOff"
                  type="number"
                  min="0"
                  step="0.5"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Risk kapalı max sermaye %</label>
                <b-form-input
                  v-model.number="form.options.strategySafetyGovernor.maxCapitalUsagePercentWhenRiskOff"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Family max loss rate</label>
                <b-form-input
                  v-model.number="form.options.strategySafetyGovernor.familyMaxLossRate"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Guardian loss max net</label>
                <b-form-input
                  v-model.number="form.options.strategySafetyGovernor.guardianLossMaxNetUsdt"
                  type="number"
                  min="0"
                  step="5"
                />
              </b-col>
            </b-row>
            <b-form-checkbox
              v-model="form.options.autoRescanOnClose"
              switch
              class="mb-1"
            >
              <strong>TP/SL sonrası otomatik rescan</strong> — pozisyon TP veya SL ile kapanınca bu hedefin edge setini arka planda yenile.
            </b-form-checkbox>
            <b-row v-if="form.options.autoRescanOnClose">
              <b-col
                cols="12"
                md="4"
              >
                <label class="font-small-3 text-muted">Cooldown (dk)</label>
                <b-form-input
                  v-model.number="form.options.autoRescanCooldownMinutes"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
      </div>

      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="editorModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          <feather-icon icon="CheckIcon" /> {{ editorModal.id ? 'Kaydet' : 'Oluştur' }}
        </b-button>
      </div>
    </b-modal>

    <!-- ─── Bot Provision Modal ───────────────────────────────────────────── -->
    <b-modal
      v-model="provisionModal.show"
      :title="`Bot Provision — ${provisionModal.target ? provisionModal.target.name : ''}`"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-alert
        show
        variant="info"
        class="py-1 px-1 mb-2"
      >
        <small>
          <feather-icon icon="InfoIcon" /> Aktif edge'lerin <strong>scoreFinal</strong> ve pool kalite filtrelerine göre en iyi N tanesi seçilir.
          <span v-if="provisionForm.targetSignalPoolEnabled">Botlar müsait oldukça aktif edge havuzundaki taze sinyalleri alır.</span>
          <span v-else>Her edge için ayrı bir bot oluşturulur ve bu hedefe bağlanır. Edge'in sembol/timeframe/indikatör/TP/SL parametreleri bot'a miras geçer.</span>
          <br><strong>Aktif edge sayısı ({{ provisionForm.market }} · {{ provisionForm.edgeKind }}):</strong> {{ availableEdgeCount }}
        </small>
      </b-alert>
      <b-card
        no-body
        class="border bg-light p-1 mb-1"
      >
        <div class="d-flex flex-wrap align-items-center mb-1">
          <div>
            <strong>Aktif exit modu</strong>
            <div class="text-muted font-small-3">
              Provisioning draft’i bu mode gore acilir; istersen mode baseline’ini yeniden uygulayabilirsin.
            </div>
          </div>
          <b-badge
            v-if="provisionModal.strategySourceMode"
            :variant="provisionForm.exitGovernor.mode === provisionModal.strategySourceMode ? 'light-success' : 'light-warning'"
            class="ml-auto"
          >
            {{ provisionForm.exitGovernor.mode === provisionModal.strategySourceMode ? 'Strategy mode miras' : 'Target/provision override' }}
          </b-badge>
        </div>
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="mb-1"
          >
            <label class="font-small-3 text-muted">Exit modu</label>
            <b-form-select
              v-model="provisionForm.exitGovernor.mode"
              :options="exitGovernorModeOptions"
              @change="onProvisionExitModeChange"
            />
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="mb-1 d-flex align-items-end"
          >
            <b-button
              size="sm"
              variant="outline-warning"
              @click="applyProvisionExitGovernorMode(provisionForm.exitGovernor.mode)"
            >
              <feather-icon icon="RotateCcwIcon" /> Mode baseline’ini yeniden uygula
            </b-button>
          </b-col>
        </b-row>
        <div class="text-muted font-small-3">
          <div
            v-for="line in provisionExitModeSummary"
            :key="line"
            class="mb-50"
          >
            <b-badge variant="light-info">
              {{ line }}
            </b-badge>
          </div>
          <div
            v-for="line in provisionExitCooldownSummary"
            :key="line"
            class="mb-50"
          >
            <b-badge variant="light-info">
              {{ line }}
            </b-badge>
          </div>
          <b-badge
            v-if="provisionEntryPresetState.lastAppliedLabel"
            variant="light-info"
            class="mb-50"
          >
            Entry preset: {{ provisionEntryPresetState.lastAppliedLabel }}
          </b-badge>
        </div>
      </b-card>
      <b-row>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Bot Sayısı *</label>
          <b-form-input
            v-model.number="provisionForm.botCount"
            type="number"
            step="1"
            min="1"
            max="50"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Mod *</label>
          <b-form-select
            v-model="provisionForm.mode"
            :options="[{value:'TESTNET',text:'Testnet'},{value:'LIVE',text:'Live'}]"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Market *</label>
          <b-form-select
            v-model="provisionForm.market"
            :options="[{value:'FUTURES',text:'Futures'},{value:'SPOT',text:'Spot'}]"
            @change="refreshAvailableEdges"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Edge Türü *</label>
          <b-form-select
            v-model="provisionForm.edgeKind"
            :options="[{value:'NORMAL',text:'Normal'},{value:'QUICK_GAIN',text:'Quick Gain'}]"
            @change="refreshAvailableEdges"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.inheritEdgeTpSl"
            switch
          >
            <strong>TP/SL değerlerini edge'den miras al</strong>
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.exitOnIndicatorSignal"
            switch
          >
            <strong>Exit sinyaliyle pozisyondan çık</strong>
          </b-form-checkbox>
        </b-col>
        <b-col
          v-if="!provisionForm.inheritEdgeTpSl"
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">TP override %</label>
          <b-form-input
            v-model.number="provisionForm.tpPercOverride"
            type="number"
            min="0.01"
            step="0.05"
          />
        </b-col>
        <b-col
          v-if="!provisionForm.inheritEdgeTpSl"
          cols="6"
          md="3"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">SL override %</label>
          <b-form-input
            v-model.number="provisionForm.slPercOverride"
            type="number"
            min="0.01"
            step="0.05"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.targetSignalPoolEnabled"
            switch
          >
            <strong>bot müsait tüm edgeleri alabilsin.</strong>
          </b-form-checkbox>
        </b-col>
        <b-col
          v-if="provisionForm.targetSignalPoolEnabled"
          cols="12"
          class="mb-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <strong class="mb-1 d-block">Pool kalite filtresi</strong>
            <b-row>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Pool max edge</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.maxEdges"
                  type="number"
                  min="1"
                  max="50"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min score</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minScoreFinal"
                  type="number"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min WR %</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minWinRate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min net EV</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minNetEv"
                  type="number"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min trade</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minTrades"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">MFE örnek</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minMfeSamples"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">+0,50 MFE %</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minMfe50Rate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">+0,75 MFE %</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minMfe75Rate"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Avg MFE %</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.minAvgMfe"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Coin cooldown dk</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.symbolCooldownMinutes"
                  type="number"
                  min="0"
                  step="5"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Aynı yön max açık</label>
                <b-form-input
                  v-model.number="provisionForm.targetSignalPoolFilters.maxSameSideOpenPositions"
                  type="number"
                  min="0"
                  step="1"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox v-model="provisionForm.targetSignalPoolFilters.rejectLastExitSl">
                  Son işlem SL ise alma
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <b-form-checkbox
              v-model="provisionForm.strategySafetyGovernor.enabled"
              switch
              class="mb-1"
            >
              <strong>Strateji güvenlik yöneticisi</strong> — risk kapalı olsa bile pozisyon boyunu ve kötüleşen giriş ailelerini otomatik frenler.
            </b-form-checkbox>
            <b-row
              v-if="provisionForm.strategySafetyGovernor.enabled"
              class="mb-1"
            >
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Risk kapalı max zarar</label>
                <b-form-input
                  v-model.number="provisionForm.strategySafetyGovernor.maxRiskUsdtWhenRiskOff"
                  type="number"
                  min="0"
                  step="0.5"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Risk kapalı max sermaye %</label>
                <b-form-input
                  v-model.number="provisionForm.strategySafetyGovernor.maxCapitalUsagePercentWhenRiskOff"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Family max loss rate</label>
                <b-form-input
                  v-model.number="provisionForm.strategySafetyGovernor.familyMaxLossRate"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Guardian loss max net</label>
                <b-form-input
                  v-model.number="provisionForm.strategySafetyGovernor.guardianLossMaxNetUsdt"
                  type="number"
                  min="0"
                  step="5"
                />
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <div class="d-flex align-items-center mb-1">
              <strong class="mb-0 d-block">Sinyal giriş kuralları</strong>
              <b-button
                size="sm"
                variant="outline-primary"
                class="ml-auto"
                :disabled="provisionEntryPresetState.loading"
                @click="refreshProvisionEntryPresets"
              >
                <b-spinner
                  v-if="provisionEntryPresetState.loading"
                  small
                />
                <feather-icon
                  v-else
                  icon="RefreshCwIcon"
                />
                Öneri yenile
              </b-button>
            </div>
            <small class="text-muted d-block mb-1">
              Havuz açıkken de kapalıyken de botun sinyale ne kadar hızlı ve hangi fiyat sapmasıyla gireceğini kontrol eder.
            </small>
            <b-form-checkbox
              v-model="provisionEntryPresetState.autoApplyEnabled"
              switch
              class="mb-1"
              @change="onProvisionEntryAutoApplyChange"
            >
              Önerileri otomatik işle
            </b-form-checkbox>
            <div class="text-muted font-small-3 mb-1">
              <span v-if="provisionEntryPresetState.autoApplyEnabled">
                Tüm öneriler provisioning draft'a otomatik işlenir.
              </span>
              <span v-else>
                Öneriyi önce görürsün, sonra manuel uygularsın.
              </span>
            </div>
            <div
              v-if="provisionEntryPresetState.rows.length"
              class="mb-1"
            >
              <div
                v-for="(row, index) in provisionEntryPresetState.rows"
                :key="row.preset.id"
                class="border rounded bg-white px-1 py-50 mb-50"
              >
                <div class="d-flex align-items-center">
                  <div class="min-w-0">
                    <b-badge :variant="entryPresetVariant(row.severity)">
                      {{ entryPresetLabel(row.severity) }}
                    </b-badge>
                    <b-badge
                      v-if="index === 0"
                      variant="light-primary"
                      class="ml-50"
                    >
                      One cikan
                    </b-badge>
                    <b-badge
                      :variant="entryPresetScopeVariant(row.preset.scope)"
                      class="ml-50"
                    >
                      {{ entryPresetScopeLabel(row.preset.scope) }}
                    </b-badge>
                    <b-badge
                      v-if="isProvisionEntryPresetApplied(row)"
                      variant="light-success"
                      class="ml-50"
                    >
                      İşlendi
                    </b-badge>
                    <strong class="ml-50">{{ row.preset.label }}</strong>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-success"
                    class="ml-auto"
                    :disabled="isProvisionEntryPresetApplied(row)"
                    @click="applyProvisionEntryPreset(row)"
                  >
                    {{ isProvisionEntryPresetApplied(row) ? 'İşlendi' : "Draft'e işle" }}
                  </b-button>
                </div>
                <div class="font-small-3 text-muted mt-50">
                  {{ row.title }}
                </div>
                <div class="font-small-3 text-primary">
                  Neden onde: {{ row.priorityReason }}
                </div>
                <div class="font-small-3 text-muted">
                  Uygular: {{ row.actionSummary }}
                </div>
                <div class="font-small-3 text-muted">
                  Amaç: {{ row.recommendationShort }}
                </div>
                <div class="font-small-3 text-muted">
                  {{ row.patchCount }} patch · {{ row.preset.summary }}
                </div>
              </div>
            </div>
            <b-row>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Onay bekleme</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.confirmationTicks"
                  type="number"
                  min="0"
                  max="5"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks sinyal yaşı</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.maxSignalAgeBars"
                  type="number"
                  min="0"
                  max="10"
                  step="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min lehe %</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.minFavorableMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks kaçmış %</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.maxFavorableMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Maks ters %</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.maxAdverseMovePct"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox
                  v-model="provisionForm.signalEntry.routerEnabled"
                  switch
                >
                  Giriş router
                </b-form-checkbox>
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min giriş skoru</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.minEntryScore"
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Min kalan ödül/risk</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.minRemainingRewardRisk"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Geç sinyal başlangıcı %</label>
                <b-form-input
                  v-model.number="provisionForm.signalEntry.lateSignalPenaltyStartPct"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                />
              </b-col>
              <b-col
                cols="12"
                md="3"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox
                  v-model="provisionForm.signalEntry.familyWeightEnabled"
                  switch
                >
                  Aile performansı
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">
            Bot Başına Sermaye (USDT)
            <span
              v-if="provisionForm.mode === 'LIVE'"
              class="text-danger"
            >*</span>
          </label>
          <b-form-input
            v-model.number="provisionForm.capitalPerBot"
            type="number"
            step="10"
            min="0"
            :placeholder="defaultCapitalHint"
          />
          <small class="text-muted">Boş bırakırsan: hedef sermayesi / bot sayısı = {{ defaultCapitalHint }} USDT</small>
        </b-col>
        <b-col
          v-if="provisionForm.mode === 'LIVE'"
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">API Anahtarı *</label>
          <b-form-select
            v-model="provisionForm.apiKeyId"
            :options="apiKeyOptions"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Komisyon/Yön % (opsiyonel)</label>
          <b-form-input
            v-model.number="provisionForm.commissionPerSide"
            type="number"
            step="0.01"
            min="0"
            placeholder="edge'den miras"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">Kaldıraç override</label>
          <b-form-input
            v-model.number="provisionForm.leverageOverride"
            type="number"
            step="1"
            min="1"
            placeholder="edge'den miras"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-1"
        >
          <label class="font-small-3 text-muted">İsim Öneki (opsiyonel)</label>
          <b-form-input
            v-model="provisionForm.namePrefix"
            :placeholder="provisionModal.target ? provisionModal.target.name : 'Hedef adı'"
          />
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-card
            no-body
            class="border bg-light p-1"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <strong>Risk hesaplayıcı</strong>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="applyRiskCalculator"
              >
                Uygula
              </b-button>
            </div>
            <b-row>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Bütçe</label>
                <b-form-input
                  v-model.number="riskCalculator.budget"
                  type="number"
                  step="100"
                  min="0"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Bot</label>
                <b-form-input
                  v-model.number="riskCalculator.botCount"
                  type="number"
                  step="1"
                  min="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Kaldıraç</label>
                <b-form-input
                  v-model.number="riskCalculator.leverage"
                  type="number"
                  step="1"
                  min="1"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">SL %</label>
                <b-form-input
                  v-model.number="riskCalculator.slPerc"
                  type="number"
                  step="0.05"
                  min="0.01"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Risk USDT</label>
                <b-form-input
                  v-model.number="riskCalculator.riskUsdt"
                  type="number"
                  step="1"
                  min="0"
                />
              </b-col>
              <b-col
                cols="6"
                md="2"
                class="mb-1"
              >
                <label class="font-small-3 text-muted">Max sermaye %</label>
                <b-form-input
                  v-model.number="riskCalculator.maxCapitalPct"
                  type="number"
                  step="5"
                  min="1"
                  max="100"
                />
              </b-col>
            </b-row>
            <b-row class="text-center">
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">Bot başına</small><div>{{ formatNum(riskCalcResult.capitalPerBot, 2) }}</div>
              </b-col>
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">Efektif sermaye</small><div>{{ formatNum(riskCalcResult.effectiveCapital, 2) }}</div>
              </b-col>
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">Notional/bot</small><div>{{ formatNum(riskCalcResult.notionalPerBot, 2) }}</div>
              </b-col>
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">SL risk/bot</small><div>{{ formatNum(riskCalcResult.riskPerBot, 2) }}</div>
              </b-col>
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">Toplam risk</small><div>{{ formatNum(riskCalcResult.totalRisk, 2) }}</div>
              </b-col>
              <b-col
                cols="6"
                md="2"
              >
                <small class="text-muted">Günlük limit</small><div>{{ formatNum(riskCalcResult.suggestedDailyLoss, 2) }}</div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.trailStopEnabled"
            switch
          >
            <strong>Trail stop</strong>
          </b-form-checkbox>
          <b-row
            v-if="provisionForm.trailStopEnabled"
            class="mt-1"
          >
            <b-col
              cols="6"
              md="3"
            >
              <label class="font-small-3 text-muted">Trigger kâr %</label>
              <b-form-input
                v-model.number="provisionForm.trailStopConfig.triggerProfit"
                type="number"
                min="0"
                step="0.05"
              />
            </b-col>
            <b-col
              cols="6"
              md="3"
            >
              <label class="font-small-3 text-muted">Trail mesafe %</label>
              <b-form-input
                v-model.number="provisionForm.trailStopConfig.trailPercent"
                type="number"
                min="0.01"
                step="0.05"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.stepStopEnabled"
            switch
          >
            <strong>Step stop</strong>
          </b-form-checkbox>
          <b-row
            v-if="provisionForm.stepStopEnabled"
            class="mt-1"
          >
            <b-col
              v-for="(step, idx) in provisionForm.stepStopConfig.steps"
              :key="idx"
              cols="12"
              md="4"
              class="mb-1"
            >
              <b-input-group :prepend="`Kademe ${idx + 1}`">
                <b-form-input
                  v-model.number="step.atProfit"
                  type="number"
                  step="0.05"
                  min="0"
                  placeholder="Kâr %"
                />
                <b-form-input
                  v-model.number="step.moveStopTo"
                  type="number"
                  step="0.05"
                  placeholder="SL %"
                />
              </b-input-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-button
            v-b-toggle.provision-position-management
            variant="flat-secondary"
            size="sm"
            block
            class="text-left"
          >
            <feather-icon icon="ShieldIcon" /> Pozisyon / KZ Yönetimi
          </b-button>
          <b-collapse
            id="provision-position-management"
            class="mt-1"
          >
            <b-card
              no-body
              class="border bg-light p-1"
            >
              <b-row>
                <b-col
                  cols="12"
                  md="6"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.breakEven.enabled"
                    switch
                  >
                    <strong>Fee-aware break-even</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Trigger %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.breakEven.triggerProfit"
                        type="number"
                        step="0.05"
                        min="0"
                        :disabled="!provisionForm.positionManagement.breakEven.enabled"
                      />
                    </b-col>
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Buffer %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.breakEven.bufferProfit"
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!provisionForm.positionManagement.breakEven.enabled"
                      />
                    </b-col>
                    <b-col
                      cols="12"
                      class="mt-50"
                    >
                      <b-form-checkbox
                        v-model="provisionForm.positionManagement.breakEven.includeFees"
                        :disabled="!provisionForm.positionManagement.breakEven.enabled"
                      >
                        Komisyonu dahil et
                      </b-form-checkbox>
                    </b-col>
                    <b-col
                      cols="6"
                      class="mt-50"
                    >
                      <label class="font-small-3 text-muted">Buffer modu</label>
                      <b-form-select
                        v-model="provisionForm.positionManagement.breakEven.bufferMode"
                        :options="breakEvenBufferModeOptions"
                        :disabled="!provisionForm.positionManagement.breakEven.enabled"
                      />
                    </b-col>
                    <b-col
                      cols="6"
                      class="mt-50"
                    >
                      <label class="font-small-3 text-muted">Kayma tamponu %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.breakEven.slippageBufferPct"
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!provisionForm.positionManagement.breakEven.enabled || provisionForm.positionManagement.breakEven.bufferMode === 'RAW'"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.partialTp.enabled"
                    switch
                  >
                    <strong>Partial TP</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Kâr %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.partialTp.atProfit"
                        type="number"
                        step="0.05"
                        min="0.01"
                        :disabled="!provisionForm.positionManagement.partialTp.enabled"
                      />
                    </b-col>
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Kapat %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.partialTp.takePercent"
                        type="number"
                        step="5"
                        min="1"
                        max="95"
                        :disabled="!provisionForm.positionManagement.partialTp.enabled"
                      />
                    </b-col>
                    <b-col
                      cols="12"
                      class="mt-50"
                    >
                      <b-form-checkbox
                        v-model="provisionForm.positionManagement.partialTp.moveRemainderToBreakEven"
                        :disabled="!provisionForm.positionManagement.partialTp.enabled"
                      >
                        Kalanı break-even'a taşı
                      </b-form-checkbox>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.trailTp.enabled"
                    switch
                  >
                    <strong>Trail TP / profit lock</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Trigger %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.trailTp.triggerProfit"
                        type="number"
                        step="0.05"
                        min="0"
                        :disabled="!provisionForm.positionManagement.trailTp.enabled"
                      />
                    </b-col>
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Aralık %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.trailTp.trailPercent"
                        type="number"
                        step="0.05"
                        min="0.01"
                        :disabled="!provisionForm.positionManagement.trailTp.enabled"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.riskSizing.enabled"
                    switch
                  >
                    <strong>Risk bazlı qty</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Risk USDT</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.riskSizing.riskUsdtPerTrade"
                        type="number"
                        step="5"
                        min="0"
                        :disabled="!provisionForm.positionManagement.riskSizing.enabled"
                      />
                    </b-col>
                    <b-col cols="6">
                      <label class="font-small-3 text-muted">Max sermaye %</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.riskSizing.maxCapitalUsagePercent"
                        type="number"
                        step="5"
                        min="1"
                        max="100"
                        :disabled="!provisionForm.positionManagement.riskSizing.enabled"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.dailyGuard.enabled"
                    switch
                  >
                    <strong>Günlük / seri zarar freni</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col cols="4">
                      <label class="font-small-3 text-muted">Günlük max zarar</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.dailyGuard.maxDailyLossUsdt"
                        type="number"
                        step="10"
                        min="0"
                        :disabled="!provisionForm.positionManagement.dailyGuard.enabled"
                      />
                    </b-col>
                    <b-col cols="4">
                      <label class="font-small-3 text-muted">Seri zarar</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.dailyGuard.maxConsecutiveLosses"
                        type="number"
                        step="1"
                        min="1"
                        :disabled="!provisionForm.positionManagement.dailyGuard.enabled"
                      />
                    </b-col>
                    <b-col cols="4">
                      <label class="font-small-3 text-muted">Cooldown dk</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.dailyGuard.cooldownMinutes"
                        type="number"
                        step="5"
                        min="0"
                        :disabled="!provisionForm.positionManagement.dailyGuard.enabled"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.dailyProfitGuard.enabled"
                    switch
                  >
                    <strong>Günlük TP limiti</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col
                      cols="12"
                      md="4"
                    >
                      <label class="font-small-3 text-muted">Günlük max kar</label>
                      <b-form-input
                        v-model.number="provisionForm.positionManagement.dailyProfitGuard.maxDailyProfitUsdt"
                        type="number"
                        step="10"
                        min="1"
                        :disabled="!provisionForm.positionManagement.dailyProfitGuard.enabled"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  cols="12"
                  class="mb-1"
                >
                  <b-form-checkbox
                    v-model="provisionForm.positionManagement.globalExposure.enabled"
                    switch
                  >
                    <strong>Aynı coin tekrar giriş kilidi</strong>
                  </b-form-checkbox>
                  <b-row class="mt-1">
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-checkbox
                        v-model="provisionForm.positionManagement.globalExposure.blockOppositeSide"
                        :disabled="!provisionForm.positionManagement.globalExposure.enabled"
                      >
                        Ters yönü de engelle
                      </b-form-checkbox>
                    </b-col>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <label class="font-small-3 text-muted">Kapsam</label>
                      <b-form-select
                        v-model="provisionForm.positionManagement.globalExposure.scope"
                        :options="[{value:'GLOBAL',text:'Tüm hedefler/botlar'}, {value:'TARGET',text:'Sadece aynı hedef'}]"
                        :disabled="!provisionForm.positionManagement.globalExposure.enabled"
                      />
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-form-checkbox
            v-model="provisionForm.autoStart"
            switch
          >
            <strong>Oluşturduktan sonra hemen başlat</strong>
            <span
              v-if="provisionModal.target && provisionModal.target.status === 'DRAFT'"
              class="font-small-3 text-info ml-1"
            >
              (Hedef DRAFT ise ACTIVE'e geçer + cycle snapshot otomatik alınır)
            </span>
          </b-form-checkbox>
        </b-col>
      </b-row>
      <div class="text-right mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="provisionModal.show = false"
        >
          İptal
        </b-button>
        <b-button
          variant="success"
          :disabled="!canProvision || provisioning"
          @click="onProvision"
        >
          <b-spinner
            v-if="provisioning"
            small
          />
          <feather-icon
            v-else
            icon="ZapIcon"
          /> {{ provisioning ? 'Oluşturuluyor...' : `${provisionForm.botCount} Bot Oluştur` }}
        </b-button>
      </div>
    </b-modal>

    <!-- ─── Detail Modal ──────────────────────────────────────────────────── -->
    <b-modal
      v-model="detailModal.show"
      :title="`Hedef Detay — ${detail ? detail.name : ''}`"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="detail">
        <div class="d-flex align-items-center mb-1">
          <small class="text-muted">
            ID #{{ detail.id }} ·
            <span v-if="detailCycleCount > 0">Cycle #{{ detailCycleCount }}<span v-if="detailCycleStartedAt"> · {{ formatDateTime(detailCycleStartedAt) }}</span> · </span>
            Son güncelleme: {{ formatDateTime(detail.updatedAt) }}
          </small>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="ml-auto"
            :disabled="refreshing"
            @click="onRecompute"
          >
            <feather-icon
              icon="RefreshCwIcon"
              :class="refreshing ? 'spinner-border spinner-border-sm' : ''"
            />
            {{ refreshing ? 'Hesaplanıyor...' : 'Yenile' }}
          </b-button>
          <b-button
            variant="outline-success"
            size="sm"
            class="ml-1"
            :disabled="reconciling"
            @click="onReconcileBots"
          >
            <b-spinner
              v-if="reconciling"
              small
            />
            <feather-icon
              v-else
              icon="ShuffleIcon"
            />
            {{ reconciling ? 'Eşitleniyor...' : 'Botları Eşitle' }}
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            class="ml-1"
            :disabled="backtestLoading"
            @click="onRunBacktest"
          >
            <b-spinner
              v-if="backtestLoading"
              small
            />
            <feather-icon
              v-else
              icon="ActivityIcon"
            />
            {{ backtestLoading ? 'Backtest...' : 'Backtest' }}
          </b-button>
        </div>

        <b-card
          no-body
          class="mb-2 bg-light-info"
        >
          <b-card-body class="py-1">
            <b-row class="text-center">
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Durum</small>
                <h5><b-badge :variant="statusVariant(detail.status)">
                  {{ detail.status }}
                </b-badge></h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Realize PnL</small>
                <h5 :class="(Number(detail.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatNum(detail.realizedPnl, 4) }}
                </h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Hedef</small>
                <h5>{{ formatNum(detail.profitTarget, 2) }}{{ detail.profitTargetIsPct ? '%' : '$' }}</h5>
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <small class="text-muted">Sermaye</small>
                <h5>{{ formatNum(detail.initialCapital, 2) }}</h5>
              </b-col>
            </b-row>
            <b-row
              v-if="detail.startAt || detail.endAt"
              class="text-center mt-1"
            >
              <b-col cols="12">
                <small class="text-muted">
                  {{ formatDateTime(detail.startAt) }} → {{ formatDateTime(detail.endAt) }}
                </small>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- Progress: profit hedefine ilerleme + maxLoss eşiği -->
        <b-card
          no-body
          class="mb-2"
        >
          <b-card-body class="py-1">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <small class="text-muted">
                Profit hedefine ilerleme
                <span v-if="detail.profitTargetIsPct">(% kar / sermaye)</span>
                <span v-else>($ kar)</span>
              </small>
              <strong :class="progressTextClass">
                {{ progressLabel }}
              </strong>
            </div>
            <b-progress
              :max="100"
              height="14px"
            >
              <b-progress-bar
                :value="Math.max(0, profitProgressPct)"
                :variant="profitProgressVariant"
                :animated="detail.status === 'ACTIVE' && profitProgressPct < 100"
              />
            </b-progress>
            <div
              v-if="detail.maxLossLimit !== null && detail.maxLossLimit !== undefined"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-1">
                <small class="text-muted">Zarar limiti kullanımı</small>
                <strong :class="lossProgressPct > 50 ? 'text-danger' : 'text-warning'">
                  {{ formatNum(Math.min(100, lossProgressPct), 1) }}%
                </strong>
              </div>
              <b-progress
                :max="100"
                height="10px"
              >
                <b-progress-bar
                  :value="Math.min(100, lossProgressPct)"
                  variant="danger"
                />
              </b-progress>
            </div>
          </b-card-body>
        </b-card>

        <b-alert
          v-if="backtestJob && !backtestResult && (backtestJob.status === 'QUEUED' || backtestJob.status === 'RUNNING')"
          show
          variant="info"
          class="mb-2"
        >
          <div class="d-flex align-items-center">
            <b-spinner
              small
              class="mr-1"
            />
            <div>
              <strong>Historical Backtest</strong>
              <small class="d-block">
                Job {{ backtestJob.id }} · {{ backtestJob.status }}
                <span v-if="backtestJob.startedAt"> · {{ formatDateTime(backtestJob.startedAt) }}</span>
              </small>
            </div>
          </div>
        </b-alert>

        <b-alert
          v-if="backtestJob && backtestJob.status === 'FAILED'"
          show
          variant="danger"
          class="mb-2"
        >
          <strong>Historical Backtest</strong>
          <small class="d-block">{{ backtestJob.error || 'Backtest başarısız.' }}</small>
        </b-alert>

        <b-alert
          v-if="backtestResult"
          show
          :variant="backtestSummaryVariant"
          class="mb-2"
        >
          <div class="d-flex align-items-center mb-1">
            <div>
              <strong>Historical Backtest</strong>
              <small class="d-block">
                {{ backtestResult.config.botCount }} bot · {{ backtestResult.config.providerKind }} ·
                {{ backtestResult.config.candleLimit }} mum
              </small>
            </div>
            <b-badge
              :variant="statusVariant(backtestResult.summary.status)"
              class="ml-auto"
            >
              {{ backtestResult.summary.status }}
            </b-badge>
          </div>
          <div
            v-if="backtestResult.summary.diffSummary"
            class="d-flex flex-wrap align-items-center text-muted font-small-3 mb-1"
          >
            <b-badge
              :variant="backtestResult.summary.diffSummary.mode === 'PARITY' ? 'info' : 'secondary'"
              class="mr-2"
            >
              {{ backtestResult.summary.diffSummary.mode }}
            </b-badge>
            <span class="mr-3">Olay {{ backtestResult.summary.diffSummary.timelineEvents }}</span>
            <span class="mr-3">Kapanış {{ backtestResult.summary.diffSummary.tradeCloses }}</span>
            <span class="mr-3">Snapshot {{ backtestResult.summary.diffSummary.closeSnapshots || 0 }}</span>
            <span>Blok {{ backtestResult.summary.diffSummary.blocks }}</span>
          </div>
          <b-row class="text-center">
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <small>Realize PnL</small>
              <h5 :class="backtestResult.summary.realizedPnl >= 0 ? 'text-success' : 'text-danger'">
                {{ signedNum(backtestResult.summary.realizedPnl, 4) }}
              </h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <small>Sermaye %</small>
              <h5 :class="backtestResult.summary.pctOfCapital >= 0 ? 'text-success' : 'text-danger'">
                {{ signedNum(backtestResult.summary.pctOfCapital, 2) }}%
              </h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <small>Trade / Win Rate</small>
              <h5>{{ backtestResult.summary.totalTrades }} · {{ formatNum(backtestResult.summary.winRate, 1) }}%</h5>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <small>Max DD</small>
              <h5 class="text-warning">
                {{ formatNum(backtestResult.summary.maxDrawdown, 4) }}
              </h5>
            </b-col>
          </b-row>
          <small
            v-if="backtestResult.summary.reason"
            class="d-block"
          >
            Sebep: <strong>{{ backtestResult.summary.reason }}</strong>
            <span v-if="backtestResult.summary.completedAt"> · {{ formatDateTime(backtestResult.summary.completedAt) }}</span>
          </small>
          <small
            v-if="backtestResult.summary.skippedFutureTrades"
            class="d-block text-muted"
          >
            Hedef eşiği sonrası {{ backtestResult.summary.skippedFutureTrades }} ileri trade simülasyondan kesildi.
          </small>
          <small
            v-if="backtestResult.summary.marketConsensusEntryBlocks"
            class="d-block text-info"
          >
            Piyasa yönü onayı {{ backtestResult.summary.marketConsensusEntryBlocks }} girişi çoğunluk veya veri yetersizliği nedeniyle bekletti.
          </small>
          <small
            v-if="backtestResult.summary.diffSummary && backtestResult.summary.diffSummary.note"
            class="d-block text-muted"
          >
            {{ backtestResult.summary.diffSummary.note }}
          </small>

          <h6 class="mt-2 mb-1">
            Bot Bazlı Sonuç
          </h6>
          <b-table
            :items="backtestResult.botResults || []"
            :fields="backtestBotFields"
            small
            responsive
          >
            <template #cell(bot)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="font-small-2 text-muted">
                {{ data.item.symbol || '—' }} · {{ data.item.timeframe || '—' }} · {{ data.item.mode }}
              </div>
              <div
                v-if="data.item.status === 'SKIPPED'"
                class="font-small-2 text-danger"
              >
                {{ data.item.skippedReason }}
              </div>
            </template>
            <template #cell(pnl)="data">
              <span :class="data.item.realizedPnl >= 0 ? 'text-success' : 'text-danger'">
                {{ signedNum(data.item.realizedPnl, 4) }}
              </span>
            </template>
            <template #cell(stats)="data">
              {{ data.item.totalTrades }} trade · {{ formatNum(data.item.winRate, 1) }}%
            </template>
          </b-table>

          <h6 class="mt-2 mb-1">
            Son Trade Timeline
          </h6>
          <b-table
            :items="backtestResult.timeline || []"
            :fields="backtestTimelineFields"
            small
            responsive
            show-empty
            empty-text="Trade yok."
          >
            <template #cell(bot)="data">
              <strong>{{ data.item.botName }}</strong>
              <div class="font-small-2 text-muted">
                {{ data.item.symbol }} · {{ data.item.timeframe }} · {{ data.item.side }}
              </div>
            </template>
            <template #cell(window)="data">
              <div>{{ formatDateTime(data.item.entryTime) }}</div>
              <div class="text-muted">
                → {{ formatDateTime(data.item.exitTime) }}
              </div>
            </template>
            <template #cell(pnl)="data">
              <span :class="data.item.pnl >= 0 ? 'text-success' : 'text-danger'">
                {{ signedNum(data.item.pnl, 4) }}
              </span>
            </template>
          </b-table>
        </b-alert>

        <h6 class="mt-1">
          <feather-icon icon="CpuIcon" /> Atanmış Botlar ({{ (detail.bots || []).length }})
        </h6>
        <b-table
          :items="detail.bots || []"
          :fields="botFields"
          small
          striped
          responsive
          show-empty
          empty-text="Hedefe henüz bot atanmadı. Bot oluştururken 'Target' alanından bağlayabilirsin."
        >
          <template #cell(name)="data">
            <strong>{{ data.item.name }}</strong>
            <div
              v-if="data.item.indicator"
              class="font-small-2 text-muted"
            >
              {{ data.item.indicator.name }} · {{ data.item.symbol || '—' }} · {{ data.item.timeframe || '—' }}
            </div>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="botStatusVariant(data.item.status)">
              {{ data.item.status }}
            </b-badge>
          </template>
          <template #cell(realizedPnl)="data">
            <span :class="(Number(data.item.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'">
              {{ formatNum(data.item.realizedPnl, 4) }}
            </span>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BButton, BBadge, BSpinner,
  BFormInput, BFormSelect, BFormCheckbox, BFormTextarea,
  BInputGroup, BInputGroupAppend,
  BRow, BCol, BModal, BAlert,
  BProgress, BProgressBar,
  BCollapse, VBToggle,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axiosIns from '@/libs/axios'
import { getSocket } from '@/libs/socket'
import { applyEntryQualityPreset, extractEntryPresetRows } from '@/utils/entryQualityPresets'
import {
  strategySafetyGovernorDecisionReason as buildStrategySafetyGovernorCoordinationDecision,
  strategySafetyGovernorCoordinationState as buildStrategySafetyGovernorCoordinationState,
  strategySafetyGovernorCoordinationSummary as buildStrategySafetyGovernorCoordinationSummary,
} from '@/utils/strategySafetyGovernorCoordination'
import {
  EXIT_GOVERNOR_MODE_OPTIONS,
  applyExitGovernorModePreset,
  exitGovernorModeLabel,
  exitGovernorReentrySummary,
  exitGovernorSummary,
  resolveExitGovernorMode,
} from '@/utils/exitGovernorModes'

export default {
  name: 'TargetsIndex',
  components: {
    BCard,
    BCardBody,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BCol,
    BModal,
    BAlert,
    BProgress,
    BProgressBar,
    BCollapse,
  },
  directives: { 'b-toggle': VBToggle },
  data() {
    return {
      loading: false,
      searchTimer: null,
      filter: { status: undefined, search: '' },
      statusOptions: [
        { value: undefined, text: 'Tümü' },
        { value: 'DRAFT', text: 'Taslak' },
        { value: 'ACTIVE', text: 'Aktif' },
        { value: 'COMPLETED', text: 'Tamamlandı' },
        { value: 'STOPPED', text: 'Durduruldu' },
        { value: 'FAILED', text: 'Başarısız' },
      ],
      exitGovernorModeOptions: EXIT_GOVERNOR_MODE_OPTIONS,
      breakEvenBufferModeOptions: [
        { value: 'NET', text: 'Net hedef' },
        { value: 'RAW', text: 'Ham fiyat' },
      ],
      fields: [
        { key: 'id', label: '#', thClass: 'width-50' },
        { key: 'name', label: 'Hedef' },
        { key: 'profit', label: 'Kar Hedefi / Sermaye' },
        { key: 'progress', label: 'İlerleme', thClass: 'width-120' },
        { key: 'bots', label: 'Botlar', thClass: 'width-110' },
        { key: 'window', label: 'Pencere', thClass: 'width-140' },
        { key: 'status', label: 'Durum', thClass: 'width-110 text-center' },
        { key: 'actions', label: '', thClass: 'width-340 text-nowrap' },
      ],
      botFields: [
        { key: 'id', label: '#' },
        { key: 'name', label: 'Bot' },
        { key: 'status', label: 'Durum' },
        { key: 'realizedPnl', label: 'Realize PnL' },
      ],
      backtestBotFields: [
        { key: 'bot', label: 'Bot' },
        { key: 'pnl', label: 'PnL' },
        { key: 'stats', label: 'Trade / WR' },
        { key: 'maxDrawdown', label: 'Max DD' },
      ],
      backtestTimelineFields: [
        { key: 'bot', label: 'Bot' },
        { key: 'window', label: 'Zaman' },
        { key: 'reason', label: 'Çıkış' },
        { key: 'pnl', label: 'PnL' },
      ],
      editorModal: {
        show: false, id: null, locked: false, strategySourceMode: null,
      },
      form: {
        name: '',
        description: '',
        initialCapital: 1000,
        profitTarget: 5,
        profitTargetIsPct: true,
        maxLossLimit: null,
        startAt: '',
        endAt: '',
        autoRepeat: false,
        expectedReturn: null,
        options: {
          lossCoverStop: false,
          allLosingAutoStop: false,
          autoRescanOnClose: false,
          autoRescanCooldownMinutes: 15,
          adaptiveAvailableMargin: { enabled: false, minMarginPercent: 60, safetyBufferPercent: 3, restoreWithRealizedProfit: true },
          dailyProfitGivebackGuard: { enabled: false, minProfitToArmUsdt: 0, maxGivebackPercent: 50 },
          exitGovernor: { mode: 'BALANCED' },
          positionGuardian: {
            enabled: true,
            authority: 'AUTO_FULL',
            profile: 'BALANCED',
            coordinateMfeControls: true,
            mfeProgressWatchEnabled: true,
            mfeProgressWatchStart: 0.30,
            mfeProgressWatchMinutes: 15,
            mfeProgressMinBridgePercent: 55,
            mfeProgressMaxLoss: 0.10,
            recoveryAssistEnabled: false,
            recoveryAssistMode: 'SUGGEST_ONLY',
            recoverySameSideFailureFlip: true,
            recoveryAllowSameSymbolHedge: false,
            recoveryWeaknessLossPct: 0.15,
            recoveryWeaknessLookbackBars: 12,
            recoveryMinSameSideFailures: 1,
            recoveryMaxRiskPct: 25,
            recoveryCooldownMinutes: 30,
            lossInsuranceEnabled: false,
            insurancePremiumPercent: 20,
            insuranceDamageRepaymentPercent: 35,
            insuranceReserveTargetPercent: 8,
            insuranceMaxOpenDamageUsdt: 100,
            insuranceDamageRiskReductionPercent: 25,
            insuranceRecoveryRiskCapPercent: 15,
            insuranceMaxRecoveryPerDay: 1,
            closeFollowEnabled: false,
            closeFollowUnrealizedConfidencePct: 60,
            closeFollowEntryPausePct: 50,
            closeFollowLossUsageTriggerPct: 70,
            closeFollowReleasePct: 50,
            closeFollowMinNetProfitUsdt: 5,
            closeFollowWinnerLockPct: 70,
            closeFollowCloseOnPositiveBasket: true,
            closeFollowProtectWinners: true,
            closeFollowPauseRecovery: true,
            closeFollowRequireApproval: false,
            closeFollowMaxClosesPerCycle: 3,
            closeFollowMinPositionAgeMinutes: 2,
            oppositePnlFuseEnabled: false,
            oppositePnlFuseThresholdPct: 0.96,
          },
          targetSignalPoolEnabled: false,
          targetSignalPoolFilters: {
            maxEdges: 10,
            minScoreFinal: 0.2,
            minWinRate: 55,
            minNetEv: 0.02,
            minTrades: 10,
            minMfeSamples: 5,
            minMfe50Rate: 30,
            minMfe75Rate: 10,
            minAvgMfe: 0.30,
            rejectLastExitSl: true,
            symbolCooldownMinutes: 30,
            maxSameSideOpenPositions: 1,
          },
          signalEntry: {
            confirmationTicks: 1,
            maxSignalAgeBars: 2,
            minFavorableMovePct: 0,
            maxFavorableMovePct: 0.30,
            maxAdverseMovePct: 0.10,
            routerEnabled: true,
            minEntryScore: 0.30,
            minRemainingRewardRisk: 0.45,
            lateSignalPenaltyStartPct: 45,
            familyWeightEnabled: true,
            familyLookback: 50,
            familyMinSamples: 5,
            familyMinWeight: 0.65,
            familyMaxWeight: 1.20,
          },
          marketDirectionConsensus: {
            enabled: false,
            minSignals: 5,
            requiredAgreementPercent: 65,
            freshnessBars: 1,
            stabilityChecks: 2,
            insufficientDataAction: 'WAIT',
          },
          strategySafetyGovernor: {
            enabled: true,
            enforceHiddenRiskCap: true,
            maxRiskUsdtWhenRiskOff: 5,
            maxCapitalUsagePercentWhenRiskOff: 35,
            minNetProfitBufferUsdt: 0.10,
            familyCircuitBreakerEnabled: true,
            familyLookbackTrades: 30,
            familyMinSamples: 5,
            familyMaxLossRate: 0.70,
            familyMinProfitFactor: 0.70,
            guardianLossCircuitBreakerEnabled: true,
            guardianLossLookbackTrades: 12,
            guardianLossMaxCount: 6,
            guardianLossMaxNetUsdt: 60,
            cooldownMinutes: 30,
          },
        },
      },
      detailModal: { show: false },
      refreshing: false,
      reconciling: false,
      backtestLoading: false,
      backtestJob: null,
      backtestResult: null,
      // ─── Provision modal ────────────────────────────────────────────
      provisionModal: { show: false, target: null, strategySourceMode: null },
      provisionForm: {
        botCount: 3,
        mode: 'TESTNET',
        market: 'FUTURES',
        edgeKind: 'NORMAL',
        exitGovernor: { mode: 'BALANCED' },
        capitalPerBot: null,
        apiKeyId: null,
        commissionPerSide: null,
        leverageOverride: null,
        namePrefix: '',
        targetSignalPoolEnabled: false,
        dailyProfitGivebackGuard: { enabled: false, minProfitToArmUsdt: 0, maxGivebackPercent: 50 },
        targetSignalPoolFilters: {
          maxEdges: 10,
          minScoreFinal: 0.2,
          minWinRate: 55,
          minNetEv: 0.02,
          minTrades: 10,
          minMfeSamples: 5,
          minMfe50Rate: 30,
          minMfe75Rate: 10,
          minAvgMfe: 0.30,
          rejectLastExitSl: true,
          symbolCooldownMinutes: 30,
          maxSameSideOpenPositions: 1,
        },
        signalEntry: {
          confirmationTicks: 1,
          maxSignalAgeBars: 2,
          minFavorableMovePct: 0,
          maxFavorableMovePct: 0.30,
          maxAdverseMovePct: 0.10,
          routerEnabled: true,
          minEntryScore: 0.30,
          minRemainingRewardRisk: 0.45,
          lateSignalPenaltyStartPct: 45,
          familyWeightEnabled: true,
          familyLookback: 50,
          familyMinSamples: 5,
          familyMinWeight: 0.65,
          familyMaxWeight: 1.20,
        },
        marketDirectionConsensus: {
          enabled: false,
          minSignals: 5,
          requiredAgreementPercent: 65,
          freshnessBars: 1,
          stabilityChecks: 2,
          insufficientDataAction: 'WAIT',
        },
        strategySafetyGovernor: {
          enabled: true,
          enforceHiddenRiskCap: true,
          maxRiskUsdtWhenRiskOff: 5,
          maxCapitalUsagePercentWhenRiskOff: 35,
          minNetProfitBufferUsdt: 0.10,
          familyCircuitBreakerEnabled: true,
          familyLookbackTrades: 30,
          familyMinSamples: 5,
          familyMaxLossRate: 0.70,
          familyMinProfitFactor: 0.70,
          guardianLossCircuitBreakerEnabled: true,
          guardianLossLookbackTrades: 12,
          guardianLossMaxCount: 6,
          guardianLossMaxNetUsdt: 60,
          cooldownMinutes: 30,
        },
        trailStopEnabled: false,
        trailStopConfig: { triggerProfit: 0.75, trailPercent: 0.4 },
        stepStopEnabled: false,
        stepStopConfig: {
          steps: [
            { atProfit: 0.5, moveStopTo: 0.1 },
            { atProfit: 0.8, moveStopTo: 0.35 },
            { atProfit: 1.1, moveStopTo: 0.65 },
          ],
        },
        positionManagement: {
          breakEven: {
            enabled: false, triggerProfit: 0.35, bufferProfit: 0.05, includeFees: true, bufferMode: 'NET', slippageBufferPct: 0.05,
          },
          partialTp: {
            enabled: false, atProfit: 0.5, takePercent: 40, moveRemainderToBreakEven: true,
          },
          trailTp: { enabled: false, triggerProfit: 0.5, trailPercent: 0.35 },
          riskSizing: { enabled: false, riskUsdtPerTrade: 50, maxCapitalUsagePercent: 100 },
          dailyGuard: {
            enabled: false, maxDailyLossUsdt: 150, maxConsecutiveLosses: 2, cooldownMinutes: 60,
          },
          dailyProfitGuard: { enabled: false, maxDailyProfitUsdt: 100 },
          globalExposure: { enabled: true, blockOppositeSide: true, scope: 'GLOBAL' },
        },
        autoStart: false,
      },
      riskCalculator: {
        budget: 3400,
        botCount: 10,
        leverage: 30,
        slPerc: 0.3,
        riskUsdt: 8,
        maxCapitalPct: 15,
      },
      availableEdgeCount: 0,
      editorEntryPresetState: {
        loading: false,
        rows: [],
        autoApplyEnabled: false,
        autoAppliedPresetKeys: [],
        lastAppliedLabel: null,
        baselineSignalEntry: null,
      },
      provisionEntryPresetState: {
        loading: false,
        rows: [],
        autoApplyEnabled: false,
        autoAppliedPresetKeys: [],
        lastAppliedLabel: null,
        baselineSignalEntry: null,
        baselinePositionManagement: null,
      },
      provisioning: false,
      consensusInsufficientOptions: [
        { value: 'WAIT', text: 'Giriş için sinyal bekle' },
        { value: 'ALLOW', text: 'Girişe izin ver' },
      ],
    }
  },
  computed: {
    dataList() { return this.$store.getters['targets/dataList'] },
    metaData() { return this.$store.getters['targets/metaData'] },
    detail() { return this.$store.getters['targets/detail'] },
    saveStatus() { return this.$store.getters['targets/dataSaveStatus'] },
    canSubmit() {
      return this.form.name && this.form.initialCapital > 0 && this.form.profitTarget > 0
    },
    // Profit hedefine ilerleme: % cinsinden hedefe ne kadar yaklaşıldı
    profitProgressPct() {
      if (!this.detail) return 0
      const realized = Number(this.detail.realizedPnl) || 0
      const goal = Number(this.detail.profitTarget) || 0
      if (goal <= 0) return 0
      if (this.detail.profitTargetIsPct) {
        const cap = Number(this.detail.initialCapital) || 0
        if (cap <= 0) return 0
        const pct = (realized / cap) * 100
        return (pct / goal) * 100
      }
      return (realized / goal) * 100
    },
    progressLabel() {
      if (!this.detail) return '0%'
      const p = this.profitProgressPct
      const realized = Number(this.detail.realizedPnl) || 0
      if (this.detail.profitTargetIsPct) {
        const cap = Number(this.detail.initialCapital) || 1
        const realizedPct = (realized / cap) * 100
        return `${realizedPct >= 0 ? '+' : ''}${realizedPct.toFixed(2)}% / ${Number(this.detail.profitTarget).toFixed(2)}%  (${p.toFixed(1)}%)`
      }
      return `${realized >= 0 ? '+' : ''}${realized.toFixed(2)} / ${Number(this.detail.profitTarget).toFixed(2)}  (${p.toFixed(1)}%)`
    },
    progressTextClass() {
      const p = this.profitProgressPct
      if (p >= 100) return 'text-success'
      if (p < 0) return 'text-danger'
      return 'text-primary'
    },
    profitProgressVariant() {
      const p = this.profitProgressPct
      if (p >= 100) return 'success'
      if (p < 0) return 'danger'
      if (p >= 50) return 'info'
      return 'primary'
    },
    detailCycleCount() {
      if (!this.detail) return 0
      const cycle = (this.detail.meta && this.detail.meta.cycle) || {}
      return Number(cycle.cycleCount || 0)
    },
    detailCycleStartedAt() {
      if (!this.detail) return null
      const cycle = (this.detail.meta && this.detail.meta.cycle) || {}
      return cycle.startedAt || null
    },
    apiKeyOptions() {
      const list = this.$store.getters['apiKeys/dataList'] || []
      return [
        { value: null, text: '— seç —' },
        ...list.filter(k => k.isActive).map(k => ({
          value: k.id,
          text: `${k.provider ? k.provider.name : '?'} · ${k.label || '(etiketsiz)'}`,
        })),
      ]
    },
    targetExitModeSummary() {
      return exitGovernorSummary(this.form?.options?.exitGovernor?.mode)
    },
    targetExitCooldownSummary() {
      return exitGovernorReentrySummary(this.form?.options?.exitGovernor)
    },
    strategySafetyGovernorCoordinationSummaryLines() {
      return buildStrategySafetyGovernorCoordinationSummary((this.form && this.form.options && this.form.options.strategySafetyGovernor) || {})
    },
    strategySafetyGovernorCoordinationDecisionValue() {
      return buildStrategySafetyGovernorCoordinationDecision((this.form && this.form.options && this.form.options.strategySafetyGovernor) || {})
    },
    strategySafetyGovernorCoordinationStateValue() {
      return buildStrategySafetyGovernorCoordinationState((this.form && this.form.options && this.form.options.strategySafetyGovernor) || {})
    },
    provisionExitModeSummary() {
      return exitGovernorSummary(this.provisionForm?.exitGovernor?.mode)
    },
    provisionExitCooldownSummary() {
      return exitGovernorReentrySummary(this.provisionForm?.exitGovernor)
    },
    targetEntryPresetState() {
      const baseline = this.editorEntryPresetState.baselineSignalEntry
      const current = this.form?.options?.signalEntry
      const isDirty = baseline ? JSON.stringify(baseline) !== JSON.stringify(current) : false
      return {
        lastAppliedLabel: this.editorEntryPresetState.lastAppliedLabel,
        isDirty,
      }
    },
    defaultCapitalHint() {
      if (!this.provisionModal.target) return '0'
      const cap = Number(this.provisionModal.target.initialCapital) || 0
      const cnt = Math.max(1, Number(this.provisionForm.botCount) || 1)
      return (cap / cnt).toFixed(2)
    },
    canProvision() {
      const f = this.provisionForm
      if (!f.botCount || f.botCount < 1) return false
      if (f.targetSignalPoolEnabled) {
        if (this.availableEdgeCount < 1) return false
      } else if (this.availableEdgeCount < f.botCount) return false
      if (!f.inheritEdgeTpSl && (!(f.tpPercOverride > 0) || !(f.slPercOverride > 0))) return false
      if (f.mode === 'LIVE') {
        if (!f.apiKeyId) return false
        if (!f.capitalPerBot || f.capitalPerBot <= 0) return false
      }
      return true
    },
    riskCalcResult() {
      const budget = Math.max(0, Number(this.riskCalculator.budget) || 0)
      const botCount = Math.max(1, Number(this.riskCalculator.botCount) || 1)
      const leverage = Math.max(1, Number(this.riskCalculator.leverage) || 1)
      const slPerc = Math.max(0.0001, Number(this.riskCalculator.slPerc) || 0.0001)
      const riskUsdt = Math.max(0, Number(this.riskCalculator.riskUsdt) || 0)
      const maxCapitalPct = Math.min(100, Math.max(1, Number(this.riskCalculator.maxCapitalPct) || 100))
      const capitalPerBot = budget / botCount
      const maxCapital = capitalPerBot * (maxCapitalPct / 100)
      const capitalByRisk = riskUsdt > 0 ? (riskUsdt / (slPerc / 100)) / leverage : maxCapital
      const effectiveCapital = Math.min(maxCapital, capitalByRisk)
      const notionalPerBot = effectiveCapital * leverage
      const riskPerBot = notionalPerBot * (slPerc / 100)
      const totalRisk = riskPerBot * botCount
      return {
        capitalPerBot,
        effectiveCapital,
        notionalPerBot,
        riskPerBot,
        totalRisk,
        suggestedDailyLoss: Math.max(riskPerBot * 5, riskPerBot * Math.ceil(botCount / 2)),
      }
    },
    lossProgressPct() {
      if (!this.detail || !this.detail.maxLossLimit) return 0
      const realized = Number(this.detail.realizedPnl) || 0
      if (realized >= 0) return 0
      const limit = Number(this.detail.maxLossLimit)
      if (limit <= 0) return 0
      return (Math.abs(realized) / limit) * 100
    },
    backtestSummaryVariant() {
      if (!this.backtestResult) return 'secondary'
      if (this.backtestResult.summary.status === 'COMPLETED') return 'success'
      if (this.backtestResult.summary.status === 'FAILED') return 'danger'
      return 'info'
    },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'Başarılı', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message,
        })
      }
    },
    'detailModal.show': function (val) {
      if (!val) {
        this.unsubscribeTarget()
        this.clearBacktestPolling()
      }
    },
    'form.options.market': function (val, oldVal) {
      if (!this.editorModal.show || !val || val === oldVal) return
      this.editorEntryPresetState.autoAppliedPresetKeys = []
      this.refreshTargetEntryPresets()
    },
    'provisionForm.market': function (val, oldVal) {
      if (!this.provisionModal.show || !val || val === oldVal) return
      this.provisionEntryPresetState.autoAppliedPresetKeys = []
      this.refreshProvisionEntryPresets()
      this.refreshAvailableEdges()
    },
  },
  beforeDestroy() {
    this.unsubscribeTarget()
    this.clearBacktestPolling()
  },
  created() {
    this.fetchList()
    if (!this.$store.getters['apiKeys/dataList']?.length) {
      this.$store.dispatch('apiKeys/getDataList', {})
    }
  },
  methods: {
    async fetchList() {
      this.loading = true
      await this.$store.dispatch('targets/getDataList', this.filter)
      this.loading = false
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetchList(), 400)
    },
    openCreateModal() {
      this.resetForm()
      this.editorEntryPresetState.autoAppliedPresetKeys = []
      this.editorEntryPresetState.lastAppliedLabel = null
      this.editorEntryPresetState.baselineSignalEntry = this.cloneDraft(this.form.options.signalEntry)
      this.editorModal = {
        show: true, id: null, locked: false, strategySourceMode: null,
      }
      this.refreshTargetEntryPresets()
    },
    openEditModal(item) {
      const opts = (item.meta && item.meta.options) || {}
      const provision = (item.meta && item.meta.provision) || {}
      const strategySource = (item.meta && item.meta.strategySource) || {}
      this.form = {
        name: item.name || '',
        description: item.description || '',
        initialCapital: Number(item.initialCapital) || 0,
        profitTarget: Number(item.profitTarget) || 0,
        profitTargetIsPct: item.profitTargetIsPct !== false,
        maxLossLimit: item.maxLossLimit !== null && item.maxLossLimit !== undefined ? Number(item.maxLossLimit) : null,
        startAt: item.startAt ? String(item.startAt).slice(0, 10) : '',
        endAt: item.endAt ? String(item.endAt).slice(0, 10) : '',
        autoRepeat: !!item.autoRepeat,
        expectedReturn: item.expectedReturn !== null && item.expectedReturn !== undefined ? Number(item.expectedReturn) : null,
        options: {
          market: opts.market === 'SPOT' || provision.market === 'SPOT' ? 'SPOT' : 'FUTURES',
          exitGovernor: {
            ...(opts.exitGovernor || {}),
            mode: resolveExitGovernorMode((opts.exitGovernor || {}).mode),
          },
          stepStop: opts.stepStop ? {
            enabled: !!opts.stepStop.enabled,
            steps: Array.isArray(opts.stepStop.steps) ? opts.stepStop.steps.map(step => ({ ...step })) : [],
          } : null,
          lossCoverStop: !!opts.lossCoverStop,
          allLosingAutoStop: !!opts.allLosingAutoStop,
          autoRescanOnClose: !!opts.autoRescanOnClose,
          autoRescanCooldownMinutes: Number(opts.autoRescanCooldownMinutes || 15),
          dailyProfitGivebackGuard: this.normalizeDailyProfitGivebackGuard(
            opts.dailyProfitGivebackGuard || provision.dailyProfitGivebackGuard,
          ),
          adaptiveAvailableMargin: this.normalizeAdaptiveAvailableMargin(opts.adaptiveAvailableMargin),
          positionGuardian: this.normalizePositionGuardian(opts.positionGuardian),
          targetSignalPoolEnabled: !!opts.targetSignalPoolEnabled,
          targetSignalPoolFilters: this.normalizePoolFilters(opts.targetSignalPoolFilters),
          signalEntry: this.normalizeSignalEntry(opts.signalEntry),
          indicatorParams: this.normalizeIndicatorParams(opts.indicatorParams),
          marketDirectionConsensus: this.normalizeMarketDirectionConsensus(opts.marketDirectionConsensus),
          strategySafetyGovernor: this.normalizeStrategySafetyGovernor(opts.strategySafetyGovernor),
        },
      }
      this.editorEntryPresetState.autoAppliedPresetKeys = []
      this.editorEntryPresetState.lastAppliedLabel = null
      this.editorEntryPresetState.baselineSignalEntry = this.cloneDraft(this.form.options.signalEntry)
      this.editorModal = {
        show: true,
        id: item.id,
        locked: item.status === 'ACTIVE',
        strategySourceMode: strategySource.exitGovernorMode
          ? resolveExitGovernorMode(strategySource.exitGovernorMode)
          : null,
      }
      this.refreshTargetEntryPresets()
    },
    resetForm() {
      this.form = {
        name: '',
        description: '',
        initialCapital: 1000,
        profitTarget: 5,
        profitTargetIsPct: true,
        maxLossLimit: null,
        startAt: '',
        endAt: '',
        autoRepeat: false,
        expectedReturn: null,
        options: {
          market: 'FUTURES',
          exitGovernor: { mode: 'BALANCED' },
          stepStop: null,
          lossCoverStop: false,
          allLosingAutoStop: false,
          autoRescanOnClose: false,
          autoRescanCooldownMinutes: 15,
          adaptiveAvailableMargin: this.defaultAdaptiveAvailableMargin(),
          dailyProfitGivebackGuard: this.defaultDailyProfitGivebackGuard(),
          positionGuardian: {
            enabled: true,
            authority: 'AUTO_FULL',
            profile: 'BALANCED',
            coordinateMfeControls: true,
            mfeProgressWatchEnabled: true,
            mfeProgressWatchStart: 0.30,
            mfeProgressWatchMinutes: 15,
            mfeProgressMinBridgePercent: 55,
            mfeProgressMaxLoss: 0.10,
            recoveryAssistEnabled: false,
            recoveryAssistMode: 'SUGGEST_ONLY',
            recoverySameSideFailureFlip: true,
            recoveryAllowSameSymbolHedge: false,
            recoveryWeaknessLossPct: 0.15,
            recoveryWeaknessLookbackBars: 12,
            recoveryMinSameSideFailures: 1,
            recoveryMaxRiskPct: 25,
            recoveryCooldownMinutes: 30,
            lossInsuranceEnabled: false,
            insurancePremiumPercent: 20,
            insuranceDamageRepaymentPercent: 35,
            insuranceReserveTargetPercent: 8,
            insuranceMaxOpenDamageUsdt: 100,
            insuranceDamageRiskReductionPercent: 25,
            insuranceRecoveryRiskCapPercent: 15,
            insuranceMaxRecoveryPerDay: 1,
            closeFollowEnabled: false,
            closeFollowUnrealizedConfidencePct: 60,
            closeFollowEntryPausePct: 50,
            closeFollowLossUsageTriggerPct: 70,
            closeFollowReleasePct: 50,
            closeFollowMinNetProfitUsdt: 5,
            closeFollowWinnerLockPct: 70,
            closeFollowCloseOnPositiveBasket: true,
            closeFollowProtectWinners: true,
            closeFollowPauseRecovery: true,
            closeFollowRequireApproval: false,
            closeFollowMaxClosesPerCycle: 3,
            closeFollowMinPositionAgeMinutes: 2,
            oppositePnlFuseEnabled: false,
            oppositePnlFuseThresholdPct: 0.96,
          },
          targetSignalPoolEnabled: false,
          targetSignalPoolFilters: this.defaultPoolFilters(),
          signalEntry: this.defaultSignalEntry(),
          indicatorParams: this.defaultIndicatorParams(),
          marketDirectionConsensus: this.defaultMarketDirectionConsensus(),
          strategySafetyGovernor: this.defaultStrategySafetyGovernor(),
        },
      }
    },
    async refreshTargetEntryPresets() {
      if (!this.form?.options) return
      this.editorEntryPresetState.loading = true
      try {
        const response = await axiosIns.post('/indicators/signal-outcomes/summary', {
          market: this.resolveTargetEntryPresetMarket(),
          hours: 72,
          limit: 25,
        })
        this.editorEntryPresetState.rows = extractEntryPresetRows(response.data.data || {}, 6)
          .map(row => ({
            ...row,
            preset: {
              ...row.preset,
              patches: (row.preset?.patches || []).filter(patch => this.isTargetEntryPresetPatch(patch)),
            },
          }))
          .filter(row => row.preset.patches.length > 0)
          .slice(0, 4)
        this.maybeAutoApplyTargetEntryPreset()
      } catch {
        this.editorEntryPresetState.rows = []
        this.showToast({
          variant: 'warning',
          title: 'Öneri yok',
          icon: 'AlertTriangleIcon',
          text: 'Target entry tuning önerileri alınamadı.',
        })
      } finally {
        this.editorEntryPresetState.loading = false
      }
    },
    cloneDraft(value) {
      return value ? JSON.parse(JSON.stringify(value)) : value
    },
    resolveTargetEntryPresetMarket() {
      return this.form?.options?.market === 'SPOT' ? 'SPOT' : 'FUTURES'
    },
    onTargetEntryAutoApplyChange(enabled) {
      if (!enabled) {
        this.editorEntryPresetState.autoAppliedPresetKeys = []
        return
      }
      this.maybeAutoApplyTargetEntryPreset()
    },
    maybeAutoApplyTargetEntryPreset() {
      if (!this.editorEntryPresetState.autoApplyEnabled) return
      this.editorEntryPresetState.rows.forEach(row => {
        if (!row?.preset?.id || this.isTargetEntryPresetApplied(row)) return
        this.applyTargetEntryPreset(row, { silent: true })
      })
    },
    applyTargetEntryPreset(row, options = {}) {
      if (!this.form?.options || !row?.preset) return
      const supportedPreset = {
        ...row.preset,
        patches: (row.preset.patches || []).filter(patch => this.isTargetEntryPresetPatch(patch)),
      }
      if (!supportedPreset.patches.length) return
      applyEntryQualityPreset(this.form.options, supportedPreset)
      this.form.options.signalEntry = this.normalizeSignalEntry(this.form.options.signalEntry)
      this.form.options.indicatorParams = this.normalizeIndicatorParams(this.form.options.indicatorParams)
      this.targetEntryPresetState.lastAppliedLabel = row.preset.label
      this.editorEntryPresetState.lastAppliedLabel = row.preset.label
      const presetKey = this.targetEntryPresetKey(row)
      if (!this.editorEntryPresetState.autoAppliedPresetKeys.includes(presetKey)) {
        this.editorEntryPresetState.autoAppliedPresetKeys.push(presetKey)
      }
      if (!options.silent) {
        this.showToast({
          variant: 'success',
          title: 'Preset işlendi',
          icon: 'CheckIcon',
          text: `${row.preset.label} target draft'ine uygulandı. Kaydetmeden kalıcı olmaz.`,
        })
      }
    },
    targetEntryPresetKey(row) {
      const targetKey = this.editorModal?.id || this.form?.name || 'target-draft'
      return `${targetKey}:${row?.preset?.id || ''}`
    },
    isTargetEntryPresetApplied(row) {
      return this.editorEntryPresetState.autoAppliedPresetKeys.includes(this.targetEntryPresetKey(row))
    },
    onTargetExitModeChange(mode) {
      this.applyTargetExitGovernorMode(mode)
    },
    applyTargetExitGovernorMode(mode) {
      const normalized = resolveExitGovernorMode(mode)
      this.form.options.exitGovernor = {
        ...(this.form.options.exitGovernor || {}),
        mode: normalized,
      }
      const modePreset = applyExitGovernorModePreset({
        positionGuardian: this.normalizePositionGuardian(this.form.options.positionGuardian),
        positionManagement: this.normalizePositionManagement(this.form.options.positionManagement),
        stepStop: this.form.options.stepStop || {},
        exitGovernor: this.form.options.exitGovernor || {},
      }, normalized)
      this.form.options.positionGuardian = this.normalizePositionGuardian(modePreset.positionGuardian)
      this.form.options.positionManagement = this.normalizePositionManagement(modePreset.positionManagement)
      this.form.options.stepStop = {
        enabled: modePreset.stepStop.enabled,
        steps: (modePreset.stepStop.steps || []).map(step => ({ ...step })),
      }
    },
    async onSubmit() {
      const body = {
        name: this.form.name,
        description: this.form.description || null,
        initialCapital: this.form.initialCapital,
        profitTarget: this.form.profitTarget,
        profitTargetIsPct: !!this.form.profitTargetIsPct,
        maxLossLimit: this.form.maxLossLimit || null,
        startAt: this.form.startAt || null,
        endAt: this.form.endAt || null,
        autoRepeat: !!this.form.autoRepeat,
        expectedReturn: this.form.expectedReturn || null,
        options: {
          market: this.form.options.market === 'SPOT' ? 'SPOT' : 'FUTURES',
          exitGovernor: {
            ...(this.form.options.exitGovernor || {}),
            mode: resolveExitGovernorMode(this.form.options.exitGovernor.mode),
          },
          stepStop: this.form.options.stepStop ? {
            enabled: !!this.form.options.stepStop.enabled,
            steps: Array.isArray(this.form.options.stepStop.steps)
              ? this.form.options.stepStop.steps.map(step => ({ ...step }))
              : [],
          } : null,
          lossCoverStop: !!this.form.options.lossCoverStop,
          allLosingAutoStop: !!this.form.options.allLosingAutoStop,
          autoRescanOnClose: !!this.form.options.autoRescanOnClose,
          autoRescanCooldownMinutes: Number(this.form.options.autoRescanCooldownMinutes || 15),
          dailyProfitGivebackGuard:
            this.normalizeDailyProfitGivebackGuard(this.form.options.dailyProfitGivebackGuard),
          adaptiveAvailableMargin:
            this.normalizeAdaptiveAvailableMargin(this.form.options.adaptiveAvailableMargin),
          positionGuardian: this.normalizePositionGuardian(this.form.options.positionGuardian),
          targetSignalPoolEnabled: !!this.form.options.targetSignalPoolEnabled,
          targetSignalPoolFilters: this.normalizePoolFilters(this.form.options.targetSignalPoolFilters),
          signalEntry: this.normalizeSignalEntry(this.form.options.signalEntry),
          indicatorParams: this.normalizeIndicatorParams(this.form.options.indicatorParams),
          marketDirectionConsensus:
            this.normalizeMarketDirectionConsensus(this.form.options.marketDirectionConsensus),
          strategySafetyGovernor:
            this.normalizeStrategySafetyGovernor(this.form.options.strategySafetyGovernor),
        },
      }
      try {
        if (this.editorModal.id) {
          await this.$store.dispatch('targets/update', { id: this.editorModal.id, body })
        } else {
          await this.$store.dispatch('targets/create', body)
        }
        this.editorModal.show = false
      } catch (e) { /* toast already shown */ }
    },
    async onShowDetail(item) {
      this.detailModal.show = true
      this.clearBacktestPolling()
      this.backtestJob = null
      this.backtestResult = null
      await this.$store.dispatch('targets/getDetail', item.id)
      await this.resumeLatestBacktest(item.id)
      this.subscribeTarget(item.id)
    },
    async resumeLatestBacktest(targetId) {
      const job = await this.$store.dispatch('targets/getLatestBacktestJob', targetId)
      if (!job) return
      this.backtestJob = job
      if (job.status === 'DONE') {
        this.backtestResult = job.result
        this.backtestLoading = false
        return
      }
      if (job.status === 'QUEUED' || job.status === 'RUNNING') {
        this.backtestLoading = true
        this.pollBacktestJob(job.id)
      }
    },
    subscribeTarget(targetId) {
      const sock = getSocket()
      if (!sock) return
      this.unsubscribeTarget()
      this._targetSubId = targetId
      sock.emit('target:subscribe', targetId)
      this._targetListener = () => {
        // Recompute zaten target.realizedPnl'i güncelliyor — sadece detail'i çek
        this.$store.dispatch('targets/getDetail', targetId)
        this.$store.dispatch('targets/getDataList', this.filter)
      }
      sock.on('target:update', this._targetListener)
    },
    unsubscribeTarget() {
      const sock = getSocket()
      if (!sock || !this._targetListener) return
      sock.off('target:update', this._targetListener)
      if (this._targetSubId) sock.emit('target:unsubscribe', this._targetSubId)
      this._targetSubId = null
      this._targetListener = null
    },
    // ─── Bot Provisioning ────────────────────────────────────────────────
    openProvisionModal(target) {
      const targetOptions = (target.meta && target.meta.options) || {}
      const provision = (target.meta && target.meta.provision) || {}
      const strategySource = (target.meta && target.meta.strategySource) || {}
      const exitMode = resolveExitGovernorMode((targetOptions.exitGovernor || provision.exitGovernor || {}).mode)
      const modePreset = applyExitGovernorModePreset({
        positionGuardian: this.normalizePositionGuardian(targetOptions.positionGuardian),
      }, exitMode)
      this.provisionEntryPresetState.autoAppliedPresetKeys = []
      this.provisionEntryPresetState.lastAppliedLabel = null
      this.provisionEntryPresetState.baselineSignalEntry = this.cloneDraft(targetOptions.signalEntry || this.defaultSignalEntry())
      this.provisionEntryPresetState.baselinePositionManagement = this.cloneDraft(targetOptions.positionManagement || this.defaultPositionManagement())
      this.provisionForm = {
        botCount: 3,
        mode: 'TESTNET',
        market: targetOptions.market === 'SPOT' || provision.market === 'SPOT' ? 'SPOT' : 'FUTURES',
        edgeKind: 'NORMAL',
        exitGovernor: {
          ...((targetOptions.exitGovernor || provision.exitGovernor || {})),
          mode: exitMode,
        },
        capitalPerBot: null,
        apiKeyId: null,
        commissionPerSide: null,
        leverageOverride: null,
        namePrefix: '',
        targetSignalPoolEnabled: !!targetOptions.targetSignalPoolEnabled,
        dailyProfitGivebackGuard: this.normalizeDailyProfitGivebackGuard(
          targetOptions.dailyProfitGivebackGuard || provision.dailyProfitGivebackGuard,
        ),
        targetSignalPoolFilters: this.normalizePoolFilters(targetOptions.targetSignalPoolFilters),
        signalEntry: this.normalizeSignalEntry(targetOptions.signalEntry),
        indicatorParams: this.normalizeIndicatorParams(targetOptions.indicatorParams || provision.indicatorParams),
        marketDirectionConsensus:
          this.normalizeMarketDirectionConsensus(targetOptions.marketDirectionConsensus),
        strategySafetyGovernor:
          this.normalizeStrategySafetyGovernor(targetOptions.strategySafetyGovernor),
        inheritEdgeTpSl: targetOptions.inheritEdgeTpSl !== false,
        exitOnIndicatorSignal: targetOptions.exitOnIndicatorSignal !== false,
        tpPercOverride: null,
        slPercOverride: null,
        trailStopEnabled: modePreset.positionManagement.trailTp.enabled === true,
        trailStopConfig: {
          triggerProfit: Number(modePreset.positionManagement.trailTp.triggerProfit || 0.5),
          trailPercent: Number(modePreset.positionManagement.trailTp.trailPercent || 0.35),
        },
        stepStopEnabled: modePreset.stepStop.enabled === true,
        stepStopConfig: {
          steps: (modePreset.stepStop.steps || []).map(step => ({ ...step })),
        },
        positionManagement: this.normalizePositionManagement(modePreset.positionManagement),
        autoStart: false,
      }
      this.riskCalculator = {
        budget: Number(target.initialCapital) || 3400,
        botCount: 10,
        leverage: 30,
        slPerc: 0.3,
        riskUsdt: 8,
        maxCapitalPct: 15,
      }
      this.provisionModal = {
        show: true,
        target,
        strategySourceMode: strategySource.exitGovernorMode
          ? resolveExitGovernorMode(strategySource.exitGovernorMode)
          : null,
      }
      this.refreshProvisionEntryPresets()
      this.refreshAvailableEdges()
    },
    onProvisionExitModeChange(mode) {
      this.applyProvisionExitGovernorMode(mode)
    },
    applyProvisionExitGovernorMode(mode) {
      const modePreset = applyExitGovernorModePreset({}, mode)
      this.provisionForm.exitGovernor = {
        ...(this.provisionForm.exitGovernor || {}),
        mode: resolveExitGovernorMode(mode),
      }
      this.provisionForm.positionManagement = this.normalizePositionManagement(modePreset.positionManagement)
      this.provisionForm.trailStopEnabled = modePreset.positionManagement.trailTp.enabled === true
      this.provisionForm.trailStopConfig = {
        triggerProfit: Number(modePreset.positionManagement.trailTp.triggerProfit || 0.5),
        trailPercent: Number(modePreset.positionManagement.trailTp.trailPercent || 0.35),
      }
      this.provisionForm.stepStopEnabled = modePreset.stepStop.enabled === true
      this.provisionForm.stepStopConfig = {
        steps: (modePreset.stepStop.steps || []).map(step => ({ ...step })),
      }
      this.showToast({
        variant: 'success',
        title: 'Exit modu işlendi',
        icon: 'CheckIcon',
        text: `${exitGovernorModeLabel(mode)} baseline’i provisioning draft’ine uygulandi.`,
      })
    },
    async refreshAvailableEdges() {
      const result = await this.$store.dispatch('targets/getAvailableEdges', {
        market: this.provisionForm.market,
        kind: this.provisionForm.edgeKind,
      })
      this.availableEdgeCount = result?.count || 0
    },
    async refreshProvisionEntryPresets() {
      this.provisionEntryPresetState.loading = true
      try {
        const response = await axiosIns.post('/indicators/signal-outcomes/summary', {
          market: this.provisionForm.market || 'FUTURES',
          hours: 72,
          limit: 25,
        })
        this.provisionEntryPresetState.rows = extractEntryPresetRows(response.data.data || {}, 4)
        this.maybeAutoApplyProvisionEntryPreset()
      } catch {
        this.provisionEntryPresetState.rows = []
        this.showToast({
          variant: 'warning',
          title: 'Öneri yok',
          icon: 'AlertTriangleIcon',
          text: 'Provision entry tuning önerileri alınamadı.',
        })
      } finally {
        this.provisionEntryPresetState.loading = false
      }
    },
    onProvisionEntryAutoApplyChange(enabled) {
      if (!enabled) {
        this.provisionEntryPresetState.autoAppliedPresetKeys = []
        return
      }
      this.maybeAutoApplyProvisionEntryPreset()
    },
    maybeAutoApplyProvisionEntryPreset() {
      if (!this.provisionEntryPresetState.autoApplyEnabled) return
      this.provisionEntryPresetState.rows.forEach(row => {
        if (!row?.preset?.id || this.isProvisionEntryPresetApplied(row)) return
        this.applyProvisionEntryPreset(row, { silent: true })
      })
    },
    applyProvisionEntryPreset(row, options = {}) {
      if (!row?.preset) return
      applyEntryQualityPreset(this.provisionForm, row.preset)
      this.provisionForm.signalEntry = this.normalizeSignalEntry(this.provisionForm.signalEntry)
      this.provisionForm.indicatorParams = this.normalizeIndicatorParams(this.provisionForm.indicatorParams)
      this.provisionForm.positionManagement = this.normalizePositionManagement(this.provisionForm.positionManagement)
      this.provisionEntryPresetState.lastAppliedLabel = row.preset.label
      const presetKey = this.provisionEntryPresetKey(row)
      if (!this.provisionEntryPresetState.autoAppliedPresetKeys.includes(presetKey)) {
        this.provisionEntryPresetState.autoAppliedPresetKeys.push(presetKey)
      }
      if (!options.silent) {
        this.showToast({
          variant: 'success',
          title: 'Preset işlendi',
          icon: 'CheckIcon',
          text: `${row.preset.label} provisioning draft'ine uygulandı. Botları oluşturmadan önce son değerleri gözden geçirebilirsin.`,
        })
      }
    },
    provisionEntryPresetKey(row) {
      const targetKey = this.provisionModal?.target?.id || this.provisionModal?.target?.name || 'provision'
      return `${targetKey}:${row?.preset?.id || ''}`
    },
    isProvisionEntryPresetApplied(row) {
      return this.provisionEntryPresetState.autoAppliedPresetKeys.includes(this.provisionEntryPresetKey(row))
    },
    async onProvision() {
      if (!this.provisionModal.target || !this.canProvision) return
      this.provisioning = true
      try {
        const body = {
          botCount: this.provisionForm.botCount,
          mode: this.provisionForm.mode,
          market: this.provisionForm.market,
          edgeKind: this.provisionForm.edgeKind,
          exitGovernor: {
            ...(this.provisionForm.exitGovernor || {}),
            mode: resolveExitGovernorMode(this.provisionForm.exitGovernor.mode),
          },
          targetSignalPoolEnabled: !!this.provisionForm.targetSignalPoolEnabled,
          dailyProfitGivebackGuard:
            this.normalizeDailyProfitGivebackGuard(this.provisionForm.dailyProfitGivebackGuard),
          targetSignalPoolFilters: this.normalizePoolFilters(this.provisionForm.targetSignalPoolFilters),
          signalEntry: this.normalizeSignalEntry(this.provisionForm.signalEntry),
          indicatorParams: this.normalizeIndicatorParams(this.provisionForm.indicatorParams),
          marketDirectionConsensus:
            this.normalizeMarketDirectionConsensus(this.provisionForm.marketDirectionConsensus),
          strategySafetyGovernor:
            this.normalizeStrategySafetyGovernor(this.provisionForm.strategySafetyGovernor),
          inheritEdgeTpSl: this.provisionForm.inheritEdgeTpSl !== false,
          exitOnIndicatorSignal: this.provisionForm.exitOnIndicatorSignal !== false,
          autoStart: !!this.provisionForm.autoStart,
        }
        if (this.provisionForm.capitalPerBot && this.provisionForm.capitalPerBot > 0) {
          body.capitalPerBot = this.provisionForm.capitalPerBot
        }
        if (this.provisionForm.commissionPerSide !== null && this.provisionForm.commissionPerSide !== '') {
          body.commissionPerSide = this.provisionForm.commissionPerSide
        }
        if (this.provisionForm.leverageOverride !== null && this.provisionForm.leverageOverride !== '') {
          body.leverageOverride = this.provisionForm.leverageOverride
        }
        if (!body.inheritEdgeTpSl) {
          if (this.provisionForm.tpPercOverride > 0) body.tpPercOverride = this.provisionForm.tpPercOverride
          if (this.provisionForm.slPercOverride > 0) body.slPercOverride = this.provisionForm.slPercOverride
        }
        if (this.provisionForm.apiKeyId) body.apiKeyId = this.provisionForm.apiKeyId
        if (this.provisionForm.namePrefix) body.namePrefix = this.provisionForm.namePrefix
        body.trailStopEnabled = !!this.provisionForm.trailStopEnabled
        if (body.trailStopEnabled) body.trailStopConfig = this.provisionForm.trailStopConfig
        body.stepStopEnabled = !!this.provisionForm.stepStopEnabled
        if (body.stepStopEnabled) body.stepStopConfig = this.provisionForm.stepStopConfig
        body.positionManagement = this.normalizePositionManagement(this.provisionForm.positionManagement)

        const result = await this.$store.dispatch('targets/provisionBots', {
          id: this.provisionModal.target.id,
          body,
        })
        if (result?.skipped?.length) {
          this.showToast({
            variant: 'warning',
            title: 'Bazıları atlandı',
            icon: 'AlertCircleIcon',
            text: `${result.skipped.length} edge atlandı (geçersiz parametre).`,
          })
        }
        this.provisionModal.show = false
      } catch (e) { /* toast already shown */ } finally {
        this.provisioning = false
      }
    },

    applyRiskCalculator() {
      const r = this.riskCalcResult
      this.provisionForm.botCount = Math.max(1, Number(this.riskCalculator.botCount) || 1)
      this.provisionForm.capitalPerBot = Number(r.capitalPerBot.toFixed(2))
      this.provisionForm.leverageOverride = Math.max(1, Number(this.riskCalculator.leverage) || 1)
      this.provisionForm.positionManagement.riskSizing.enabled = true
      this.provisionForm.positionManagement.riskSizing.riskUsdtPerTrade = Math.max(0, Number(this.riskCalculator.riskUsdt) || 0)
      this.provisionForm.positionManagement.riskSizing.maxCapitalUsagePercent = Math.min(100, Math.max(1, Number(this.riskCalculator.maxCapitalPct) || 100))
      this.provisionForm.positionManagement.dailyGuard.enabled = true
      this.provisionForm.positionManagement.dailyGuard.maxDailyLossUsdt = Number(r.suggestedDailyLoss.toFixed(2))
    },

    defaultPoolFilters() {
      return {
        maxEdges: 10,
        minScoreFinal: 0.2,
        minWinRate: 55,
        minNetEv: 0.02,
        minTrades: 10,
        minMfeSamples: 5,
        minMfe50Rate: 30,
        minMfe75Rate: 10,
        minAvgMfe: 0.30,
        rejectLastExitSl: true,
        symbolCooldownMinutes: 30,
        maxSameSideOpenPositions: 1,
      }
    },

    defaultDailyProfitGivebackGuard() {
      return { enabled: false, minProfitToArmUsdt: 0, maxGivebackPercent: 50 }
    },

    defaultAdaptiveAvailableMargin() {
      return {
        enabled: false,
        minMarginPercent: 60,
        safetyBufferPercent: 3,
        restoreWithRealizedProfit: true,
      }
    },

    defaultSignalEntry() {
      return {
        confirmationTicks: 1,
        maxSignalAgeBars: 2,
        minFavorableMovePct: 0,
        maxFavorableMovePct: 0.30,
        maxAdverseMovePct: 0.10,
        routerEnabled: true,
        minEntryScore: 0.30,
        minRemainingRewardRisk: 0.45,
        lateSignalPenaltyStartPct: 45,
        familyWeightEnabled: true,
        familyLookback: 50,
        familyMinSamples: 5,
        familyMinWeight: 0.65,
        familyMaxWeight: 1.20,
      }
    },
    defaultIndicatorParams() {
      return {}
    },
    normalizeIndicatorParams(value) {
      const v = value && typeof value === 'object' ? value : {}
      const out = { ...v }
      const numberIn = (key, min, max) => {
        const raw = Number(v[key])
        if (!Number.isFinite(raw)) return undefined
        return Math.min(max, Math.max(min, raw))
      }
      if (v.momentumQualityEnabled !== undefined) {
        out.momentumQualityEnabled = v.momentumQualityEnabled === true
      }
      const minBody = numberIn('minMomentumBodyRatio', 0, 1)
      if (minBody !== undefined) out.minMomentumBodyRatio = minBody
      const minClose = numberIn('minMomentumCloseLocation', 0, 1)
      if (minClose !== undefined) out.minMomentumCloseLocation = minClose
      return out
    },
    isTargetEntryPresetPatch(patch) {
      const path = String(patch?.path || '')
      return path.startsWith('signalEntry.') || path.startsWith('indicatorParams.')
    },

    defaultMarketDirectionConsensus() {
      return {
        enabled: false,
        minSignals: 5,
        requiredAgreementPercent: 65,
        freshnessBars: 1,
        stabilityChecks: 2,
        insufficientDataAction: 'WAIT',
      }
    },

    defaultStrategySafetyGovernor() {
      return {
        enabled: true,
        enforceHiddenRiskCap: true,
        maxRiskUsdtWhenRiskOff: 5,
        maxCapitalUsagePercentWhenRiskOff: 35,
        minNetProfitBufferUsdt: 0.10,
        familyCircuitBreakerEnabled: true,
        familyLookbackTrades: 30,
        familyMinSamples: 5,
        familyMaxLossRate: 0.70,
        familyMinProfitFactor: 0.70,
        guardianLossCircuitBreakerEnabled: true,
        guardianLossLookbackTrades: 12,
        guardianLossMaxCount: 6,
        guardianLossMaxNetUsdt: 60,
        cooldownMinutes: 30,
      }
    },

    normalizeMarketDirectionConsensus(value) {
      const d = this.defaultMarketDirectionConsensus()
      const v = value || {}
      const numberIn = (key, min, max) => {
        const raw = Number(v[key] ?? d[key])
        return Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : d[key]
      }
      return {
        enabled: v.enabled === true,
        minSignals: Math.floor(numberIn('minSignals', 1, 100)),
        requiredAgreementPercent: numberIn('requiredAgreementPercent', 50, 100),
        freshnessBars: numberIn('freshnessBars', 0.25, 10),
        stabilityChecks: Math.floor(numberIn('stabilityChecks', 1, 10)),
        insufficientDataAction: v.insufficientDataAction === 'ALLOW' ? 'ALLOW' : 'WAIT',
      }
    },

    normalizeStrategySafetyGovernor(value) {
      const d = this.defaultStrategySafetyGovernor()
      const v = value || {}
      const numberIn = (key, min, max) => {
        const raw = Number(v[key] ?? d[key])
        return Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : d[key]
      }
      return {
        enabled: v.enabled !== false,
        enforceHiddenRiskCap: v.enforceHiddenRiskCap !== false,
        maxRiskUsdtWhenRiskOff: numberIn('maxRiskUsdtWhenRiskOff', 0, 1000),
        maxCapitalUsagePercentWhenRiskOff: numberIn('maxCapitalUsagePercentWhenRiskOff', 0, 100),
        minNetProfitBufferUsdt: numberIn('minNetProfitBufferUsdt', 0, 1000),
        familyCircuitBreakerEnabled: v.familyCircuitBreakerEnabled !== false,
        familyLookbackTrades: Math.floor(numberIn('familyLookbackTrades', 5, 500)),
        familyMinSamples: Math.floor(numberIn('familyMinSamples', 0, 100)),
        familyMaxLossRate: numberIn('familyMaxLossRate', 0, 1),
        familyMinProfitFactor: numberIn('familyMinProfitFactor', 0, 10),
        guardianLossCircuitBreakerEnabled: v.guardianLossCircuitBreakerEnabled !== false,
        guardianLossLookbackTrades: Math.floor(numberIn('guardianLossLookbackTrades', 1, 100)),
        guardianLossMaxCount: Math.floor(numberIn('guardianLossMaxCount', 0, 100)),
        guardianLossMaxNetUsdt: numberIn('guardianLossMaxNetUsdt', 0, 10000),
        cooldownMinutes: Math.floor(numberIn('cooldownMinutes', 0, 1440)),
      }
    },

    normalizeSignalEntry(value) {
      const d = this.defaultSignalEntry()
      const v = value || {}
      const numberIn = (key, min, max) => {
        const raw = Number(v[key] ?? d[key])
        if (!Number.isFinite(raw)) return d[key]
        return Math.min(max, Math.max(min, raw))
      }
      return {
        confirmationTicks: Math.floor(numberIn('confirmationTicks', 0, 5)),
        maxSignalAgeBars: Math.floor(numberIn('maxSignalAgeBars', 0, 10)),
        minFavorableMovePct: numberIn('minFavorableMovePct', 0, 5),
        maxFavorableMovePct: numberIn('maxFavorableMovePct', 0, 10),
        maxAdverseMovePct: numberIn('maxAdverseMovePct', 0, 10),
        routerEnabled: v.routerEnabled !== false,
        minEntryScore: numberIn('minEntryScore', 0, 1),
        minRemainingRewardRisk: numberIn('minRemainingRewardRisk', 0, 10),
        lateSignalPenaltyStartPct: numberIn('lateSignalPenaltyStartPct', 0, 100),
        familyWeightEnabled: v.familyWeightEnabled !== false,
        familyLookback: Math.floor(numberIn('familyLookback', 5, 500)),
        familyMinSamples: Math.floor(numberIn('familyMinSamples', 0, 100)),
        familyMinWeight: numberIn('familyMinWeight', 0.1, 2),
        familyMaxWeight: numberIn('familyMaxWeight', 0.1, 3),
      }
    },

    normalizePoolFilters(value) {
      const d = this.defaultPoolFilters()
      const v = value || {}
      const numberOrDefault = (raw, fallback) => {
        const next = Number(raw ?? fallback)
        return Number.isFinite(next) ? next : fallback
      }
      return {
        maxEdges: Math.min(50, Math.max(1, numberOrDefault(v.maxEdges, d.maxEdges))),
        minScoreFinal: numberOrDefault(v.minScoreFinal, d.minScoreFinal),
        minWinRate: Math.min(100, Math.max(0, numberOrDefault(v.minWinRate, d.minWinRate))),
        minNetEv: numberOrDefault(v.minNetEv, d.minNetEv),
        minTrades: Math.max(0, numberOrDefault(v.minTrades, d.minTrades)),
        minMfeSamples: Math.max(0, numberOrDefault(v.minMfeSamples, d.minMfeSamples)),
        minMfe50Rate: Math.min(100, Math.max(0, numberOrDefault(v.minMfe50Rate, d.minMfe50Rate))),
        minMfe75Rate: Math.min(100, Math.max(0, numberOrDefault(v.minMfe75Rate, d.minMfe75Rate))),
        minAvgMfe: Math.max(0, numberOrDefault(v.minAvgMfe, d.minAvgMfe)),
        rejectLastExitSl: v.rejectLastExitSl !== undefined ? !!v.rejectLastExitSl : d.rejectLastExitSl,
        symbolCooldownMinutes: Math.max(0, numberOrDefault(v.symbolCooldownMinutes, d.symbolCooldownMinutes)),
        maxSameSideOpenPositions: Math.max(0, numberOrDefault(v.maxSameSideOpenPositions, d.maxSameSideOpenPositions)),
      }
    },

    normalizeDailyProfitGivebackGuard(value) {
      const d = this.defaultDailyProfitGivebackGuard()
      const v = value || {}
      const minProfit = Number(v.minProfitToArmUsdt ?? d.minProfitToArmUsdt)
      const giveback = Number(v.maxGivebackPercent ?? d.maxGivebackPercent)
      return {
        enabled: v.enabled === true,
        minProfitToArmUsdt: Number.isFinite(minProfit) ? Math.max(0, minProfit) : d.minProfitToArmUsdt,
        maxGivebackPercent: Number.isFinite(giveback) ? Math.min(100, Math.max(0, giveback)) : d.maxGivebackPercent,
      }
    },

    normalizeAdaptiveAvailableMargin(value) {
      const d = this.defaultAdaptiveAvailableMargin()
      const v = value || {}
      const minMargin = Number(v.minMarginPercent ?? d.minMarginPercent)
      const buffer = Number(v.safetyBufferPercent ?? d.safetyBufferPercent)
      return {
        enabled: v.enabled === true,
        minMarginPercent: Number.isFinite(minMargin) ? Math.min(100, Math.max(1, minMargin)) : d.minMarginPercent,
        safetyBufferPercent: Number.isFinite(buffer) ? Math.min(50, Math.max(0, buffer)) : d.safetyBufferPercent,
        restoreWithRealizedProfit: v.restoreWithRealizedProfit !== false,
      }
    },

    normalizePositionGuardian(value) {
      const v = value || {}
      const authority = ['SUGGEST_CONFIRM', 'AUTO_STOP_ONLY', 'AUTO_FULL'].includes(v.authority) ? v.authority : 'AUTO_FULL'
      const profile = ['PROTECTIVE', 'BALANCED', 'AGGRESSIVE'].includes(v.profile) ? v.profile : 'BALANCED'
      return {
        enabled: v.enabled !== false,
        authority,
        profile,
        coordinateMfeControls: v.coordinateMfeControls !== false,
        preMfeDamageControl: v.preMfeDamageControl !== false,
        preMfeAfterMinutes: Number.isFinite(Number(v.preMfeAfterMinutes)) ? Number(v.preMfeAfterMinutes) : 20,
        preMfeMaxMfe: Number.isFinite(Number(v.preMfeMaxMfe)) ? Number(v.preMfeMaxMfe) : 0.15,
        preMfeSoftExitLoss: Number.isFinite(Number(v.preMfeSoftExitLoss)) ? Number(v.preMfeSoftExitLoss) : 0.45,
        halfSlSoftExitEnabled: v.halfSlSoftExitEnabled !== false,
        halfSlSoftExitRatio: Number.isFinite(Number(v.halfSlSoftExitRatio)) ? Number(v.halfSlSoftExitRatio) : 50,
        mfeProgressWatchEnabled: v.mfeProgressWatchEnabled !== false,
        mfeProgressWatchStart: Number.isFinite(Number(v.mfeProgressWatchStart)) ? Number(v.mfeProgressWatchStart) : 0.30,
        mfeProgressWatchMinutes: Number.isFinite(Number(v.mfeProgressWatchMinutes)) ? Number(v.mfeProgressWatchMinutes) : 15,
        mfeProgressMinBridgePercent: Number.isFinite(Number(v.mfeProgressMinBridgePercent)) ? Number(v.mfeProgressMinBridgePercent) : 55,
        mfeProgressMaxLoss: Number.isFinite(Number(v.mfeProgressMaxLoss)) ? Number(v.mfeProgressMaxLoss) : 0.10,
        minNetProfitBuffer: Number.isFinite(Number(v.minNetProfitBuffer)) ? Number(v.minNetProfitBuffer) : 0.03,
        breakEvenMoveMfe: Number.isFinite(Number(v.breakEvenMoveMfe)) ? Number(v.breakEvenMoveMfe) : 0.55,
        profitMoveMfe: Number.isFinite(Number(v.profitMoveMfe)) ? Number(v.profitMoveMfe) : 0.75,
        profitMoveMinBuffer: Number.isFinite(Number(v.profitMoveMinBuffer)) ? Number(v.profitMoveMinBuffer) : 0.10,
        profitMoveDynamicRatio: Number.isFinite(Number(v.profitMoveDynamicRatio)) ? Number(v.profitMoveDynamicRatio) : 25,
        autoTuneApplyWithoutApproval: v.autoTuneApplyWithoutApproval === true,
        walletAware: v.walletAware !== false,
        walletPressureDrawdownPct: Number.isFinite(Number(v.walletPressureDrawdownPct)) ? Number(v.walletPressureDrawdownPct) : 0.50,
        walletPressureLossMultiplier: Number.isFinite(Number(v.walletPressureLossMultiplier)) ? Number(v.walletPressureLossMultiplier) : 0.75,
        walletPressureTimeMultiplier: Number.isFinite(Number(v.walletPressureTimeMultiplier)) ? Number(v.walletPressureTimeMultiplier) : 0.50,
        tpPreservation: v.tpPreservation !== false,
        minTpDistanceForProfitClose: Number.isFinite(Number(v.minTpDistanceForProfitClose)) ? Number(v.minTpDistanceForProfitClose) : 0.25,
        minTpProgressForProfitClose: Number.isFinite(Number(v.minTpProgressForProfitClose)) ? Number(v.minTpProgressForProfitClose) : 50,
        oppositePnlFuseEnabled: v.oppositePnlFuseEnabled === true,
        oppositePnlFuseThresholdPct: Number.isFinite(Number(v.oppositePnlFuseThresholdPct)) ? Number(v.oppositePnlFuseThresholdPct) : 0.96,
        avgWinProtectionEnabled: v.avgWinProtectionEnabled !== false,
        avgWinLookbackTrades: Number.isFinite(Number(v.avgWinLookbackTrades)) ? Number(v.avgWinLookbackTrades) : 30,
        minAvgWinSamples: Number.isFinite(Number(v.minAvgWinSamples)) ? Number(v.minAvgWinSamples) : 3,
        minProfitCloseAvgWinRatio: Number.isFinite(Number(v.minProfitCloseAvgWinRatio)) ? Number(v.minProfitCloseAvgWinRatio) : 70,
        recoveryAssistEnabled: v.recoveryAssistEnabled === true,
        recoveryAssistMode: v.recoveryAssistMode === 'AUTO_OPEN' ? 'AUTO_OPEN' : 'SUGGEST_ONLY',
        recoverySameSideFailureFlip: v.recoverySameSideFailureFlip !== false,
        recoveryAllowSameSymbolHedge: v.recoveryAllowSameSymbolHedge === true,
        recoveryMinSameSideFailures: Number.isFinite(Number(v.recoveryMinSameSideFailures)) ? Number(v.recoveryMinSameSideFailures) : 1,
        recoveryWeaknessLossPct: Number.isFinite(Number(v.recoveryWeaknessLossPct)) ? Number(v.recoveryWeaknessLossPct) : 0.15,
        recoveryWeaknessLookbackBars: Number.isFinite(Number(v.recoveryWeaknessLookbackBars)) ? Number(v.recoveryWeaknessLookbackBars) : 12,
        recoveryMaxRiskPct: Number.isFinite(Number(v.recoveryMaxRiskPct)) ? Number(v.recoveryMaxRiskPct) : 25,
        recoveryCooldownMinutes: Number.isFinite(Number(v.recoveryCooldownMinutes)) ? Number(v.recoveryCooldownMinutes) : 30,
        recoveryMinCandidateScore: Number.isFinite(Number(v.recoveryMinCandidateScore)) ? Number(v.recoveryMinCandidateScore) : 0.20,
        recoveryCandidateLimit: Number.isFinite(Number(v.recoveryCandidateLimit)) ? Number(v.recoveryCandidateLimit) : 3,
        lossInsuranceEnabled: v.lossInsuranceEnabled === true,
        insurancePremiumPercent: Number.isFinite(Number(v.insurancePremiumPercent)) ? Number(v.insurancePremiumPercent) : 20,
        insuranceDamageRepaymentPercent: Number.isFinite(Number(v.insuranceDamageRepaymentPercent)) ? Number(v.insuranceDamageRepaymentPercent) : 35,
        insuranceReserveTargetPercent: Number.isFinite(Number(v.insuranceReserveTargetPercent)) ? Number(v.insuranceReserveTargetPercent) : 8,
        insuranceMaxOpenDamageUsdt: Number.isFinite(Number(v.insuranceMaxOpenDamageUsdt)) ? Number(v.insuranceMaxOpenDamageUsdt) : 100,
        insuranceDamageRiskReductionPercent: Number.isFinite(Number(v.insuranceDamageRiskReductionPercent)) ? Number(v.insuranceDamageRiskReductionPercent) : 25,
        insuranceRecoveryRiskCapPercent: Number.isFinite(Number(v.insuranceRecoveryRiskCapPercent)) ? Number(v.insuranceRecoveryRiskCapPercent) : 15,
        insuranceMaxRecoveryPerDay: Number.isFinite(Number(v.insuranceMaxRecoveryPerDay)) ? Number(v.insuranceMaxRecoveryPerDay) : 1,
        closeFollowEnabled: v.closeFollowEnabled === true,
        closeFollowUnrealizedConfidencePct: Number.isFinite(Number(v.closeFollowUnrealizedConfidencePct)) ? Number(v.closeFollowUnrealizedConfidencePct) : 60,
        closeFollowEntryPausePct: Number.isFinite(Number(v.closeFollowEntryPausePct)) ? Number(v.closeFollowEntryPausePct) : 50,
        closeFollowLossUsageTriggerPct: Number.isFinite(Number(v.closeFollowLossUsageTriggerPct)) ? Number(v.closeFollowLossUsageTriggerPct) : 70,
        closeFollowReleasePct: Number.isFinite(Number(v.closeFollowReleasePct)) ? Number(v.closeFollowReleasePct) : 50,
        closeFollowMinNetProfitUsdt: Number.isFinite(Number(v.closeFollowMinNetProfitUsdt)) ? Number(v.closeFollowMinNetProfitUsdt) : 5,
        closeFollowWinnerLockPct: Number.isFinite(Number(v.closeFollowWinnerLockPct)) ? Number(v.closeFollowWinnerLockPct) : 70,
        closeFollowCloseOnPositiveBasket: v.closeFollowCloseOnPositiveBasket !== false,
        closeFollowProtectWinners: v.closeFollowProtectWinners !== false,
        closeFollowPauseRecovery: v.closeFollowPauseRecovery !== false,
        closeFollowRequireApproval: v.closeFollowRequireApproval === true,
        closeFollowMaxClosesPerCycle: Number.isFinite(Number(v.closeFollowMaxClosesPerCycle)) ? Number(v.closeFollowMaxClosesPerCycle) : 3,
        closeFollowMinPositionAgeMinutes: Number.isFinite(Number(v.closeFollowMinPositionAgeMinutes)) ? Number(v.closeFollowMinPositionAgeMinutes) : 2,
      }
    },

    defaultPositionManagement() {
      return {
        breakEven: {
          enabled: false, triggerProfit: 0.35, bufferProfit: 0.05, includeFees: true, bufferMode: 'NET', slippageBufferPct: 0.05,
        },
        partialTp: {
          enabled: false, atProfit: 0.5, takePercent: 40, moveRemainderToBreakEven: true,
        },
        trailTp: { enabled: false, triggerProfit: 0.5, trailPercent: 0.35 },
        riskSizing: { enabled: false, riskUsdtPerTrade: 50, maxCapitalUsagePercent: 100 },
        dailyGuard: {
          enabled: false, maxDailyLossUsdt: 150, maxConsecutiveLosses: 2, cooldownMinutes: 60,
        },
        dailyProfitGuard: { enabled: false, maxDailyProfitUsdt: 100 },
        globalExposure: { enabled: true, blockOppositeSide: true, scope: 'GLOBAL' },
      }
    },
    normalizePositionManagement(value) {
      const d = this.defaultPositionManagement()
      const v = value || {}
      return {
        breakEven: { ...d.breakEven, ...(v.breakEven || {}) },
        partialTp: { ...d.partialTp, ...(v.partialTp || {}) },
        trailTp: { ...d.trailTp, ...(v.trailTp || {}) },
        riskSizing: { ...d.riskSizing, ...(v.riskSizing || {}) },
        dailyGuard: { ...d.dailyGuard, ...(v.dailyGuard || {}) },
        dailyProfitGuard: { ...d.dailyProfitGuard, ...(v.dailyProfitGuard || {}) },
        globalExposure: { ...d.globalExposure, ...(v.globalExposure || {}) },
      }
    },

    async onRecompute() {
      if (!this.detail) return
      this.refreshing = true
      try {
        await this.$store.dispatch('targets/recompute', this.detail.id)
        // Detayı tazele (bot list dahil)
        await this.$store.dispatch('targets/getDetail', this.detail.id)
      } finally {
        this.refreshing = false
      }
    },
    async onReconcileBots() {
      if (!this.detail) return
      this.reconciling = true
      try {
        const result = await this.$store.dispatch('targets/reconcileBots', this.detail.id)
        this.showToast({
          variant: 'success',
          title: 'Botlar eşitlendi',
          icon: 'ShuffleIcon',
          text: `Atanan: ${result.assigned?.length || 0}, Yeni: ${result.created?.length || 0}, Bekleyen: ${result.waiting?.length || 0}`,
        })
      } finally {
        this.reconciling = false
      }
    },
    async onRunBacktest() {
      if (!this.detail) return
      this.clearBacktestPolling()
      this.backtestLoading = true
      this.backtestJob = null
      this.backtestResult = null
      try {
        const job = await this.$store.dispatch('targets/backtest', {
          id: this.detail.id,
          body: {
            providerKind: 'BINANCE',
            candleLimit: 1500,
            startAt: this.detail.startAt || null,
            endAt: this.detail.endAt || null,
          },
        })
        this.backtestJob = job
        this.pollBacktestJob(job.id)
      } finally {
        if (!this.backtestJob) this.backtestLoading = false
      }
    },
    pollBacktestJob(jobId) {
      if (!this.detail || !jobId) return
      this.clearBacktestPolling()
      const targetId = this.detail.id
      const tick = async () => {
        try {
          const job = await this.$store.dispatch('targets/getBacktestJob', { id: targetId, jobId })
          this.backtestJob = job
          if (job.status === 'DONE') {
            this.backtestResult = job.result
            this.backtestLoading = false
            this.clearBacktestPolling()
            return
          }
          if (job.status === 'FAILED') {
            this.backtestLoading = false
            this.clearBacktestPolling()
            this.showToast({
              variant: 'danger', title: 'Backtest', icon: 'XIcon', text: job.error || 'Backtest başarısız.',
            })
            return
          }
          this._backtestPollTimer = setTimeout(tick, 2500)
        } catch (e) {
          this.backtestLoading = false
          this.clearBacktestPolling()
        }
      }
      this._backtestPollTimer = setTimeout(tick, 1000)
    },
    clearBacktestPolling() {
      if (this._backtestPollTimer) {
        clearTimeout(this._backtestPollTimer)
        this._backtestPollTimer = null
      }
    },
    async onStart(id) { await this.$store.dispatch('targets/start', id) },
    async onStop(id) { await this.$store.dispatch('targets/stop', id) },
    async onRemove(id) {
      this.$swal({
        title: 'Hedef silinsin mi?',
        text: 'Soft-delete edilir; bağlı botlar etkilenmez (hedeflerinden çözülür).',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sil',
        cancelButtonText: 'İptal',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) await this.$store.dispatch('targets/remove', id)
      })
    },

    // ─── Helpers ──────────────────────────────────────────────────────────
    canStart(item) {
      return item.status === 'DRAFT' || item.status === 'STOPPED'
    },
    cycleCountOf(item) {
      const cycle = (item && item.meta && item.meta.cycle) || {}
      return Number(cycle.cycleCount || 0)
    },
    countBotsByStatus(bots, status) {
      return (bots || []).filter(b => b.status === status).length
    },
    statusVariant(s) {
      const map = {
        DRAFT: 'secondary', ACTIVE: 'success', COMPLETED: 'info', STOPPED: 'dark', FAILED: 'danger',
      }
      return map[s] || 'secondary'
    },
    entryPresetVariant(severity) {
      if (severity === 'RISK') return 'light-danger'
      if (severity === 'WATCH') return 'light-warning'
      return 'light-success'
    },
    entryPresetLabel(severity) {
      if (severity === 'RISK') return 'Risk'
      if (severity === 'WATCH') return 'Dikkat'
      return 'Bilgi'
    },
    entryPresetScopeVariant(scope) {
      if (scope === 'GLOBAL_EXPOSURE') return 'light-info'
      if (scope === 'INDICATOR_PARAMS') return 'light-warning'
      return 'light-primary'
    },
    entryPresetScopeLabel(scope) {
      if (scope === 'GLOBAL_EXPOSURE') return 'Exposure'
      if (scope === 'INDICATOR_PARAMS') return 'Engine'
      return 'Signal Entry'
    },
    botStatusVariant(s) {
      const map = {
        IDLE: 'secondary', RUNNING: 'success', PAUSED: 'warning', STOPPED: 'dark', ERROR: 'danger',
      }
      return map[s] || 'secondary'
    },
    formatNum(v, d = 2) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(d) : '—'
    },
    signedNum(v, d = 2) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n >= 0 ? '+' : ''}${n.toFixed(d)}`
    },
    formatDate(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      } catch (e) { return String(v) }
    },
    formatDateTime(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(v) }
    },
    truncate(s, n) { return s && s.length > n ? `${s.slice(0, n)}...` : s },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>
